import { track } from '@/lib/funnel';
import { photoPos } from './lib/photoDir';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '@/lib/units';
import { useUnits } from '@/lib/units';
import DepthPrompt from '@/components/DepthPrompt';
import { Toaster, toast } from 'sonner';
import {
  FolderOpen, Plus, Trash2, MapPinned, Camera, ScanSearch, LocateFixed, ImagePlus, PenLine, Wrench, Video,
  FileText, Files, Globe2, Map as MapIcon, Save, Check, Loader2, Wifi, WifiOff, CloudDownload, Settings, CircleHelp, Bolt, History, MapPin, Navigation, Route, Share2, CircleCheck, Download, Sheet, FileSpreadsheet, PencilRuler, Earth, Satellite, Package, FolderKanban, FilePlus2, Flag, Boxes, Tags, Rocket,
  Monitor,
} from 'lucide-react';
import ProjectPrices, { countProjectPrices } from '@/components/ProjectPrices';
import { setProjectPitPrices } from '@/lib/excavation';
import '@/App.css';
import { I18N, detectInitialLanguage } from '@/lib/i18n';
import { dbGetAll, dbPut, dbDelete } from '@/lib/db';
import { newProject, newReport, ensureProjectHasReports, compressImage, uid, isLocked, mainPhotoOf } from '@/lib/model';
import { exportKMZ, exportJSON } from '@/lib/exporters';
import { exportPDF } from '@/lib/pdf';
import { exportDXF, exportGeoJSON, exportGPX, exportOfficeZip } from '@/lib/gisExport';
import MapView from '@/components/MapView';
import StartChooser, { ZONE_COLORS, getStartPref, setStartPref } from '@/components/StartChooser';
import FieldMode from '@/components/FieldMode';
import GhostCamera from '@/components/GhostCamera';
import ToolDrawer from '@/components/ToolDrawer';
import PitDialog from '@/components/PitDialog';
import CatalogDialog from '@/components/CatalogDialog';
import MfrDialog, { openMfr } from '@/components/MfrDialog';
import FirePondPlanner, { openPond } from '@/components/FirePondPlanner';
import ReportMaterials from '@/components/ReportMaterials';
import LvBox from '@/components/LvBox';
import ObjectSheet from '@/components/ObjectSheet';
import usePits from '@/hooks/usePits';
import OverlayControls from '@/components/OverlayControls';
import PhotoList from '@/components/PhotoList';
import PipePanel from '@/components/PipePanel';
import useNetwork from '@/hooks/useNetwork';
import { ShutPanel } from '@/components/NetCheck';
import NetQualityDialog, { NetQualityCard, openNetQ } from '@/components/NetQuality';
import IrrBomDialog from '@/components/IrrBom';
import IrrPlan from '@/components/IrrPlan';
import CoordList from '@/components/CoordList';
import FeedbackButton from '@/components/FeedbackButton';
import FloorPlans, { FloorBar, activePlan, onFloor } from '@/components/FloorPlans';
import SketchEditor from '@/components/SketchEditor';
import MoistGrid from '@/components/MoistGrid';
import Pano360 from '@/components/Pano360';
import PlanStart from '@/components/PlanStart';
import SitePlans from '@/components/SitePlans';
import PhotoDocBar from '@/components/PhotoDocBar';
import { docPinsOf } from '@/components/DocPins';
import { photoReportPdf } from '@/lib/photoReportPdf';
import AutoPlaceBar from '@/components/AutoPlaceBar';
import EscCloser from '@/components/EscCloser';
import PlacePicker, { pickPlace } from '@/components/PlacePicker';
import { netQuality } from '@/lib/netCheck';
import { AreaEditBar, PitEditBar } from '@/components/AreaEditor';
import { copyName } from '@/lib/areaEdit';
import { videoThumb, deserialize } from '@/lib/media';
import { VoiceRecorder, MemoList } from '@/components/VoiceMemo';
import SettingsDialog from '@/components/SettingsDialog';
import DictateField from '@/components/DictateField';
import InventoryCounter from '@/components/InventoryCounter';
import { hasModule, APP_INFO, syncLicenses, getBlocked, activateCode } from '@/lib/license';

const LIC_ERR = { device: 'licDevice', expired: 'licExpired', revoked: 'licRevoked', offline: 'licOffline', seats: 'entSeatsFull' };
import { ExpiryBanner, BlockedDialog } from '@/components/LicenseNotice';
import ManualDialog from '@/components/ManualDialog';
import { HelpBtn } from '@/components/HelpBtn';
import TourGuide, { restartTour, HelpHere } from '@/components/TourGuide';
import HintSpeaker from '@/components/HintSpeaker';
import CivilBox from '@/components/CivilBox';
import TourFlyover from '@/components/TourFlyover';
import { askConfirm } from '@/lib/askConfirm';
import TourHandoff from '@/components/TourHandoff';
import { pickupWork, cleanCode, linkKey } from '@/lib/handoff';
import { handoffOn, kritisOn, usePolicy, extOk } from '@/lib/policy';
import '@/lib/netGuard';
import ScreenDimmer from '@/components/ScreenDimmer';
import { LangSelect, HelpMenu } from '@/components/HeaderMenus';
import { effWidth } from '@/lib/uiScale';
import { UiScaleBtn } from '@/components/UiScaleBtn';
import { GloveBtn } from '@/components/GloveMode';
import SetupGuide, { resetSetup } from '@/components/SetupGuide';
import CalibReminder from '@/components/CalibReminder';
import TimesheetBox from '@/components/TimesheetBox';
import DemoMode from '@/components/DemoMode';
import FeatureTree, { openTree } from '@/components/FeatureTree';
import CountHistory from '@/components/CountHistory';
import AddressSearch from '@/components/AddressSearch';
import RoutePlanner from '@/components/RoutePlanner';
import { sharePdf } from '@/lib/share';
import { geoReverse } from '@/lib/geocode';
import { MANUAL } from '@/lib/manual';
import { exportXLSX, exportCSV, exportMaintXLSX } from '@/lib/netExport';
import OverdueCard from '@/components/OverdueCard';
import MaintStats from '@/components/MaintStats';
import AlarmCard from '@/components/AlarmCard';
import StreetViewModal from '@/components/StreetViewModal';
import AppShare from '@/components/AppShare';
import TrackSheet from '@/components/TrackSheet';
import TrackList from '@/components/TrackList';
import NaviBar from '@/components/NaviBar';
import ObjNavi from '@/components/ObjNavi';
import RoadPrefetch from '@/components/RoadPrefetch';
import WaypointFab from '@/components/WaypointFab';
import SignatureBox from '@/components/SignatureBox';
import DamageReport from '@/components/DamageReport';
import BackupReminder from '@/components/BackupReminder';
import RoofFrame from '@/components/RoofFrame';
import ImportCenter from '@/components/ImportCenter';
import GardenCalc from '@/components/GardenCalc';
import ProfilePicker, { ModeBar, StartLinkPrompt } from '@/components/ProfilePicker';
import KksBanner from '@/components/KksBanner';
import CrossBearing from '@/components/CrossBearing';
import KksPlan from '@/components/KksPlan';
import ScaffoldBanner from '@/components/ScaffoldBanner';
import PrecisionDialog, { RtkBadge } from '@/components/PrecisionDialog';
import { extRtk, measurePrecise, precProfile, hrmsMax } from '@/lib/precision';
import { tl } from '@/lib/terms';
import { plotPdf } from '@/lib/plotPdf';
import { mergeAgents, waitState, WAIT_CLR } from '@/lib/agents';
import AgentDb from '@/components/AgentDb';
import { usePanel } from '@/lib/panelCfg';
import { useMode, ml, openProfiles, applyProfiles, getMode, getSub, needsOnboarding, secOf, inMode } from '@/lib/profiles';
import { openTrack, trackRec, interrupted, staleDone } from '@/lib/trackRec';
import { placePhoto } from '@/lib/photoPlace';
import { tourBook } from '@/lib/tourBook';
import CorrCalc from '@/components/CorrCalc';
import ScaffoldRent from '@/components/ScaffoldRent';
import CableFault from '@/components/CableFault';
import { exportGasXLSX, exportLogCSV } from '@/lib/netExport';
import StakeOut from '@/components/StakeOut';
import LoggerPlan from '@/components/LoggerPlan';
import AgentButton from '@/components/AgentButton';
import { RoundImport } from '@/components/RoundShare';
import WorkLog from '@/components/WorkLog';
import FacadeStudio from '@/components/FacadeStudio';
import DrawStudio from '@/components/DrawStudio';
import PhotoDraw, { openPhotoDraw } from '@/components/PhotoDraw';
import DailyReport from '@/components/DailyReport';
import TransferDialog from '@/components/TransferDialog';
import ArchiveDialog from '@/components/ArchiveDialog';
import ProjectManager from '@/components/pm/ProjectManager';
import { PosBar } from '@/components/pm/PosBar';
import { ensurePm, stampReport, pmOf, logHist } from '@/lib/pm';
import NetShare from '@/components/NetShare';
import { NST } from '@/lib/netShareText';
import { aquaLeak } from '@/lib/leakToAqua';
import { PHT } from '@/components/PcHomeSetting';
import { pmMigrate } from '@/lib/pmMigrate';
import { applyReturn, PKG, RET } from '@/lib/pmPkg';
import { demoProjects, demoPackagesFor } from '@/lib/pmDemo';
import { pmT, fill as pmFill } from '@/lib/i18nPm';
import { ENC, openEnvelope } from '@/lib/pmCrypt';
import AgentPointer, { AgentSteps } from '@/components/AgentPointer';
import { setAgentAppCtx } from '@/lib/agentApp';
import { archiveSupported, archiveRestore, archiveReady, archiveProject, setArchiveProject, archiveBackup, autoArchive } from '@/lib/archive';
import { fieldDiff } from '@/lib/fieldDiff';
import { HO } from '@/lib/i18nHandover';
import { HandoverDiff } from '@/components/HandoverDiff';
import { useFeatures } from '@/lib/features';
import LeakPhotoPrompt from '@/components/LeakPhotoPrompt';
import GnssDialog from '@/components/GnssDialog';
import GnssLineBar from '@/components/GnssLineBar';
import useLoggers, { noiseSpots } from '@/hooks/useLoggers';
import { pipeLatLngs, haversine } from '@/lib/model';
import { len } from '@/lib/units';
import { rtkAuto } from '@/components/RtkAuto';
import { objPos } from '@/lib/maint';
import { LoggerDialog, LoggerSheet } from '@/components/LoggerUi';
import LoggerRoutes from '@/components/LoggerRoutes';
import SurveyDialog from '@/components/SurveyDialog';
import VisMenu from '@/components/VisMenu';
import { BoundaryPrompt, PrivModeBar } from '@/components/BoundaryBar';
import { openSurvey } from '@/components/SurveyDialog';
import { resolvePt } from '@/lib/model';
import { pitClashes } from '@/lib/pitClash';
import { pipeLabel } from '@/components/PitLines';
import { gnssState, gnssLive, getGnssCfg, averageGnss, gnssMeta } from '@/lib/gnss';
import AquaPanel from '@/components/AquaPanel';
import { demoNet } from '@/lib/aquaDemo';
import { inpToProject } from '@/lib/inpImport';
import { scenFits } from '@/lib/aquaNet';
import { setAqua } from '@/lib/aquaStore';
import { setSew } from '@/lib/sewStore';
import Aqua3D from '@/components/Aqua3D';
import '@/lib/i18nAqua';
import '@/lib/i18nStorm';
import '@/lib/i18nArchive';
import '@/lib/i18nPolicy';

function App() {
  const [lang, setLang] = useState(detectInitialLanguage);
  useUnits();
  const [projects, setProjects] = useState([]);
  const [projId, setProjId] = useState(null);
  useEffect(() => { setAqua({ res: null, th: null, eps: null, on: false }); setSew({ res: null, flood: null, on: false }); }, [projId]);
  const [startOpen, setStartOpen] = useState(true);
  const [agentRoute, setAgentRoute] = useState(null);
  const [flyTrack, setFlyTrack] = useState(null);
  const [showHandoff, setShowHandoff] = useState(false);
  const [handover, setHandover] = useState(null);
  const hoBusy = useRef(false);
  // toasts appear just below the (wrapping) header so they never cover its buttons
  const [toastTop, setToastTop] = useState(96);
  useEffect(() => {
    const m = () => { const h = document.querySelector('.top-bar')?.getBoundingClientRect(); setToastTop(Math.max(16, Math.min(window.innerHeight / 2, (h?.bottom || 80) + 8))); };
    m(); const iv = setInterval(m, 1500); window.addEventListener('resize', m); window.addEventListener('scroll', m, { passive: true });
    return () => { clearInterval(iv); window.removeEventListener('resize', m); window.removeEventListener('scroll', m); };
  }, []);
  useEffect(() => {
    const ho = () => setShowHandoff(true);
    const fl = () => { const a = allToursRef.current().filter((x) => x.pts?.length > 1); if (a.length) setFlyTrack(a[a.length - 1]); else toast.message(I18N[lang]?.bookNoTrack); };
    window.addEventListener('giss-handoff-open', ho); window.addEventListener('giss-fly-last', fl);
    return () => { window.removeEventListener('giss-handoff-open', ho); window.removeEventListener('giss-fly-last', fl); };
  }, [lang]); // eslint-disable-line react-hooks/exhaustive-deps
  const allToursRef = useRef(() => []);
  useEffect(() => { const on = (e) => setFlyTrack(e.detail); window.addEventListener('giss-flyover', on); return () => window.removeEventListener('giss-flyover', on); }, []);
  useEffect(() => { if (interrupted && !staleDone && !startOpen) { openTrack(); toast.warning(t.trkInterrupted, { duration: 10000 }); } }, [startOpen]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const on = (e) => { if (!e.detail?.keep) setAgentRoute(e.detail?.pts?.length ? e.detail : null); };
    const st = (e) => setAgentRoute((r) => { if (!r) return r; const n = { ...r, stops: e.detail.stops }; setTimeout(() => window.dispatchEvent(new CustomEvent('giss-agent-route', { detail: { ...n, keep: true } })), 0); return n; });
    window.addEventListener('giss-agent-route', on); window.addEventListener('giss-agent-stops', st);
    return () => { window.removeEventListener('giss-agent-route', on); window.removeEventListener('giss-agent-stops', st); };
  }, []);
  const [startPref, setPrefState] = useState(getStartPref);
  // first start: setup guide + sector choice come first, then the start chooser
  const [stcReady, setStcReady] = useState(false);
  useEffect(() => {
    if (stcReady || !startOpen) return undefined;
    const id = setInterval(() => { if (localStorage.getItem('giss_setup_done') && !document.querySelector('.su-overlay, [data-testid="setup-guide"], [data-testid="profile-close-btn"]')) setStcReady(true); }, 500);
    return () => clearInterval(id);
  }, [stcReady, startOpen]);
  useEffect(() => { document.body.classList.toggle('start-open', startOpen); }, [startOpen]);
  const [repId, setRepId] = useState(null);
  const [overlayVisible, setOverlayVisible] = useState(true);
  const [fs, setFs] = useState(false);
  const [follow, setFollow] = useState(false);
  const [sel, setSel] = useState(null);
  useEffect(() => { const h = (e) => setSel({ kind: 'fitting', id: e.detail }); window.addEventListener('giss-select-fit', h); return () => window.removeEventListener('giss-select-fit', h); }, []);
  const openFit = (f) => { document.querySelector('[data-testid="map-wrapper"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' }); mapRef.current.fitAll([[f.lat, f.lon]]); setSel({ kind: 'fitting', id: f.id }); };
  useEffect(() => { const on = (e) => setSel(e.detail); window.addEventListener('giss-select', on); return () => window.removeEventListener('giss-select', on); }, []);
  const [showShare, setShowShare] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [pmOpen, setPmOpen] = useState(false);
  const [pmPkg, setPmPkg] = useState(null);
  usePolicy();
  const [wide, setWide] = useState(() => effWidth() >= 1024);
  const [office, setOffice] = useState(() => (localStorage.getItem('giss_office') ?? String(window.innerWidth >= 1024)) === 'true');
  const [dragOn, setDragOn] = useState(false);
  const ft = useFeatures();
  const mode = useMode();
  const pv = usePanel(mode);
  const [camReq, setCamReq] = useState(null);
  const mainGalRef = useRef(null);
  const toggleFs = (on) => {
    // installed app has no browser bar: skip system fullscreen (it shows the "swipe to exit" domain notice)
    const app = window.matchMedia('(display-mode: standalone), (display-mode: fullscreen)').matches || document.referrer.startsWith('android-app://');
    if (on) { window.history.pushState({ fs: 1 }, ''); if (!app) document.documentElement.requestFullscreen?.().catch(() => {}); setFs(true); }
    else if (window.history.state?.fs) window.history.back();
    else setFs(false);
  };
  useEffect(() => {
    if (!fs) return undefined;
    document.body.classList.add('fs-lock');
    setFollow(false);
    const onPop = () => setFs(false);
    window.addEventListener('popstate', onPop);
    return () => {
      document.body.classList.remove('fs-lock');
      setCamReq(null);
      window.removeEventListener('popstate', onPop);
      if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    };
  }, [fs]);
  const [saveState, setSaveState] = useState({ saving: false, at: null });
  const [busy, setBusy] = useState(null);
  const [online, setOnline] = useState(navigator.onLine);
  const [showSettings, setShowSettings] = useState(false);
  const [, setLicTick] = useState(0);
  const [invPhoto, setInvPhoto] = useState(null);
  const [showManual, setShowManual] = useState(false);
  const [helpKey, setHelpKey] = useState(null);
  useEffect(() => { const on = (e) => { setHelpKey(e.detail); setShowManual(true); }; window.addEventListener('giss-help', on); return () => window.removeEventListener('giss-help', on); }, []);
  const [showCatalog, setShowCatalog] = useState(false);
  const [showPrices, setShowPrices] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [picked, setPicked] = useState(null);
  const [showRoute, setShowRoute] = useState(false);
  const [invKey, setInvKey] = useState(0);
  const [fitFilter, setFitFilter] = useState([]);
  const [installEvt, setInstallEvt] = useState(null);
  const invRef = useRef(null);
  const [cacheProg, setCacheProg] = useState(null);
  const mapRef = useRef(null);
  const camRef = useRef(null);
  const [pdCam, setPdCam] = useState(false);
  const [pdBusy, setPdBusy] = useState(false);
  const editAfter = useRef(false);
  const galRef = useRef(null);
  const vidRef = useRef(null);
  const jsonRef = useRef(null);
  const roleRef = useRef('detail');
  const saveTimer = useRef(null);
  useEffect(() => { archiveRestore().catch(() => {}); }, []);
  const projectsRef = useRef(projects);
  projectsRef.current = projects;
  const t = tl(lang, mode);
  useEffect(() => { const on = () => toast.error(t.krBlocked, { id: 'kritis', duration: 8000 }); window.addEventListener('giss-kritis-block', on); return () => window.removeEventListener('giss-kritis-block', on); }, [t]);
  const leisure = mode === 'outdoor';
  const slim = leisure || mode === 'photo';
  // every tour of all leisure works (oldest first), each knows its project for the photos
  const allTours = () => projects.filter((p) => p.sector === 'outdoor' || p.id === project?.id).flatMap((p) => (p.tracks || []).map((tr) => ({ ...tr, __proj: p }))).sort((a, b) => (a.start || 0) - (b.start || 0));
  allToursRef.current = allTours;

  const project = projects.find((p) => p.id === projId) || null;
  useEffect(() => { setArchiveProject(project, lang); }, [project, lang]);
  useEffect(() => { setAgentAppCtx({ project, report, t, sel, updateProject }); });
  const waitMap = useMemo(() => (mode !== 'garden' || !project ? null : Object.fromEntries((project.reports || []).map((r) => [r.id, waitState(project.tracks, r.id)]).filter(([, w]) => w).map(([id, w]) => [id, WAIT_CLR[w.st]]))), [mode, project]);
  const report = project?.reports.find((r) => r.id === repId) || project?.reports[0] || null;

  const importRef = useRef(null);
  useEffect(() => {
    const onResize = () => setWide(effWidth() >= 1024);
    const isFile = (e) => [...(e.dataTransfer?.types || [])].includes('Files');
    const over = (e) => { if (isFile(e)) { e.preventDefault(); setDragOn(true); } };
    const leave = (e) => { if (!e.relatedTarget) setDragOn(false); };
    const drop = (e) => {
      if (!isFile(e)) return;
      e.preventDefault(); setDragOn(false);
      const f = [...e.dataTransfer.files].find((x) => /\.(json|giss)$|\.giss\.txt$/i.test(x.name));
      if (f) importRef.current?.(f); else toast.error(t.trDropWrong);
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('dragover', over);
    window.addEventListener('dragleave', leave);
    window.addEventListener('drop', drop);
    return () => { window.removeEventListener('resize', onResize); window.removeEventListener('dragover', over); window.removeEventListener('dragleave', leave); window.removeEventListener('drop', drop); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { localStorage.setItem('giss_office', String(office)); setTimeout(() => window.dispatchEvent(new Event('resize')), 60); }, [office]);
  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setInstallEvt(e); };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', () => setInstallEvt(null));
    const on = () => setOnline(true), off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem('giss_reset_done')) { sessionStorage.removeItem('giss_reset_done'); toast.success(t.resetDone); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  useEffect(() => {
    const pc = cleanCode(new URLSearchParams(window.location.search).get('pickup')), key = linkKey();
    if (pc.length !== 10) return;
    window.history.replaceState(null, '', '/');
    if (kritisOn()) { setTimeout(() => toast.error(t.krBlocked, { duration: 9000 }), 1800); return; }
    setTimeout(() => { const id = toast.loading(t.hoLoading); pickupWork(pc, key, (f) => importRef.current(f, true)).then(() => toast.success(t.hoGot, { id }), (e) => toast.error(e.message === 'key' ? t.hoKeyErr : t.hoNotFound, { id })); }, 1800);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const code = q.get('license') || q.get('code');
    if (!code || window.__gissLic === code) return;
    window.__gissLic = code;
    activateCode(code).then((res) => {
      if (res.error) return toast.error(t[LIC_ERR[res.error] || 'licBad'], { duration: 8000 });
      window.history.replaceState(null, '', '/');
      toast.success(t.entLinkOk, { duration: 6000 });
      setLicTick((n) => n + 1);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const check = async () => { const bad = await syncLicenses(); if (bad.length || getBlocked().length) setLicTick((n) => n + 1); };
    const onVis = () => document.visibilityState === 'visible' && check();
    check();
    window.addEventListener('online', check);
    document.addEventListener('visibilitychange', onVis);
    return () => { window.removeEventListener('online', check); document.removeEventListener('visibilitychange', onVis); };
  }, []);

  useEffect(() => {
    localStorage.setItem('baustelle_app_lang', lang);
    window.dispatchEvent(new Event('giss-catalog'));
    document.documentElement.lang = lang;
  }, [lang]);

  const scheduleSave = useCallback((id) => {
    setSaveState((s) => ({ ...s, saving: true }));
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      const p = projectsRef.current.find((x) => x.id === id);
      if (p) await dbPut(p);
      if (p && archiveReady() && window.innerWidth >= 900) autoArchive(p);
      setSaveState({ saving: false, at: new Date() });
    }, 400);
  }, []);

  const updateProject = useCallback((fn) => {
    setProjects((prev) => prev.map((p) => (p.id === projId ? { ...fn(p), updatedAt: Date.now() } : p)));
    scheduleSave(projId);
  }, [projId, scheduleSave]);
  // sector rename: generic construction default titles (any language) -> sector terms
  const relabel = useCallback((s) => {
    if (!s) return s;
    for (const B of Object.values(I18N)) {
      for (const k of ['defaultRepName', 'defaultProjName']) if (B[k] && t[k] !== I18N[lang]?.[k] && t[k] !== B[k] && s.startsWith(B[k])) return t[k] + s.slice(B[k].length);
    }
    return s;
  }, [t, lang]);
  useEffect(() => {
    if (!project) return;
    const title = relabel(project.title), reps = project.reports.map((r) => relabel(r.title));
    if (title !== project.title || reps.some((x, i) => x !== project.reports[i].title)) updateProject((p) => ({ ...p, title, reports: p.reports.map((r, i) => ({ ...r, title: reps[i] ?? r.title })) }));
  }, [project?.id, relabel]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateReport = useCallback((patch) => {
    if (!report) return;
    updateProject((p) => ({
      ...p,
      reports: p.reports.map((r) => (r.id === report.id ? { ...r, ...(typeof patch === 'function' ? patch(r) : patch), updatedAt: Date.now() } : r)),
    }));
  }, [report, updateProject]);

  const focusReport = (r) => r && mapRef.current?.setView(r.lat, r.lon, r.zoom);

  useEffect(() => {
    (async () => {
      // never hang on the spinner: storage errors / slow IndexedDB (Android WebView) fall back to a fresh project
      const slow = new Promise((r) => setTimeout(() => r(null), 4000));
      let all = await Promise.race([(window.__gissLoad ||= dbGetAll()).catch(() => null), slow]);
      if (!all) toast.error(t.dbFail, { duration: 10000 });
      all = (all || []).filter((p) => p && p.id && Array.isArray(p.reports));
      if (!all.length) {
        const p = (window.__gissFirst ||= { ...newProject(lang), __fresh: true });
        dbPut(p).catch(() => {});
        all = [p];
      }
      all.forEach((p) => { try { ensureProjectHasReports(p, lang); } catch { /* keep */ } });
      all = all.filter((p) => p.reports?.length);
      all = await pmMigrate(all).catch(() => all);
      all.sort((a, b) => b.updatedAt - a.updatedAt);
      setProjects(all);
      setProjId(all[0].id);
      setRepId(all[0].reports[0].id);
      focusReport(all[0].reports[0]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const net = useNetwork({ t, project, updateProject, mapRef, repId: report?.id });
  setProjectPitPrices(project?.prices?.pits);
  const pits = usePits({ t, project, updateProject, net, repId, mapRef });
  const onPinClick = useCallback((id) => {
    const r = projectsRef.current.find((p) => p.id === projId)?.reports.find((x) => x.id === id);
    if (lg.armRef.current) return r && addPointRef.current([r.lat, r.lon]);
    if (net.draftRef.current) return r && net.addPoint([r.lat, r.lon]);
    selectReport(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projId]);
  const lg = useLoggers({ t, project, updateProject, repId: report?.id });
  // starting another placement tool cancels a pending logger arm (otherwise the next tap opens the logger dialog)
  useEffect(() => { if (net.draft) lg.setArm(null); }, [net.draft]); // eslint-disable-line react-hooks/exhaustive-deps
  const noise = useMemo(() => noiseSpots(project), [project?.loggers]); // eslint-disable-line react-hooks/exhaustive-deps
  const [gnssLine, setGnssLine] = useState(false);
  const addPointRef = useRef(null);
  const pickRef = useRef(null);
  const pendRef = useRef(null);
  const [bnd, setBnd] = useState(null);
  const [privMode, setPrivMode] = useState(false);
  const privRef = useRef(false);
  privRef.current = privMode;
  useEffect(() => {
    const d = net.draft;
    if (d?.kind === 'fitting' && !!d.priv !== privMode) net.setDraft({ ...d, priv: privMode });
  }, [privMode, net.draft]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const on = (e) => {
      const mode = e.detail, d = net.draftRef.current;
      if (mode === 'survey' || mode === 'dock') {
        let pt;
        if (mode === 'dock') {
          const pp = (sel?.kind === 'pipe' && project.pipes.find((x) => x.id === sel.id)) || project.pipes[project.pipes.length - 1];
          if (!pp) return toast.error(t.plcNoPipe);
          pt = resolvePt(pp.points[pp.points.length - 1], project.fittings);
          const P = pipeLatLngs(pp, project.fittings), a = P[P.length - 2], b = P[P.length - 1];
          pendRef.current = { mode, pipeId: pp.id, dn: pp.dn || '', angle: a && b ? Math.round((Math.atan2((b[1] - a[1]) * Math.cos((a[0] * Math.PI) / 180), b[0] - a[0]) * 180) / Math.PI + 360) % 360 : 0 };
        } else { const c = mapRef.current.getCenter(); pt = [c.lat, c.lng]; pendRef.current = { mode }; }
        return net.addPoint(pt, undefined, { free: true });
      }
      if ((mode === 'gg' || mode === 'gk') && d?.kind === 'pipe' && d.points.length) {
        const c = mapRef.current.getCenter(), fid = uid('fit');
        const f = { id: fid, type: mode, name: `${t['fit_' + mode]} ${project.fittings.length + 1}`, sym: mode.toUpperCase(), state: 'none', note: '', lat: c.lat, lon: c.lng, media: [], memos: [], reportId: report?.id };
        updateProject((p) => ({ ...p, fittings: [...(p.fittings || []), f] }));
        net.finishPipe([...d.points, { fid }]);
        setBnd({ kind: mode, fid, type: d.type, color: d.color });
      }
    };
    window.addEventListener('giss-place', on);
    return () => window.removeEventListener('giss-place', on);
  });
  useEffect(() => {
    const lf = net.lastFit;
    if (!lf || !project) return;
    const f = project.fittings.find((x) => x.id === lf.id);
    const pd = pendRef.current;
    pendRef.current = null;
    if (!f) return;
    if (pd?.mode === 'survey') openSurvey({ kind: 'fitting', id: f.id });
    if (pd?.mode === 'dock') {
      net.updatePipe(pd.pipeId, (p) => (Array.isArray(p.points[p.points.length - 1]) ? { points: [...p.points.slice(0, -1), { fid: f.id }] } : {}));
      net.updateFitting(f.id, { hostPipe: pd.pipeId, hostDn: pd.dn, angle: pd.angle });
    }
    if ((f.hostPipe || pd?.mode === 'dock') && (['tee3', 'cross4', 'house'].includes(f.type) || ['AB', 'AS', 'T'].includes(f.sym))) setSel({ kind: 'fitting', id: f.id });
  }, [net.lastFit]); // eslint-disable-line react-hooks/exhaustive-deps
  addPointRef.current = (pt) => {
    if (pickRef.current) { const f = pickRef.current; pickRef.current = null; return f(pt); }
    if (lg.armRef.current && !net.draftRef.current) return lg.onMap(pt);
    const d = net.draftRef.current, s = gnssState();
    // survey profile only: RTK FIX nearby -> receiver position (announced); plan profile keeps every tapped point
    if (d && (d.kind === 'pipe' || d.kind === 'fitting') && precProfile() === 'survey' && rtkAuto() && gnssLive(s) && !s.est && !s.sim && s.fix === 'rtk' && s.hrms != null && s.hrms <= hrmsMax() && haversine(pt, [s.lat, s.lon]) < 25) {
      const g = gnssMeta(s, { profile: 'survey', qs: 'ok' });
      toast.success(`${t.rtkTaken}${g.acc != null ? ` (± ${len(g.acc, 2)})` : ''}`, { id: 'rtk-auto', duration: 2500 });
      return net.addPoint([g.lat, g.lon], undefined, d.kind === 'fitting' ? { gnss: g } : undefined);
    }
    return net.addPoint(pt);
  };
  const clashSig = useRef({ pid: null, m: {} });
  useEffect(() => {
    if (!project) return;
    const c = clashSig.current, fresh = c.pid !== project.id;
    if (fresh) c.m = {};
    c.pid = project.id;
    (project.pits || []).forEach((p) => {
      const hits = pitClashes(p, project);
      const sig = hits.map((h) => h.pipe.id).sort().join(',');
      if (!fresh && sig && sig !== c.m[p.id]) toast.error(`⚠️ ${t.plAlarm} (${hits.map((h) => pipeLabel(h.pipe, t)).join(', ')})`, { duration: 12000, id: `clash-${p.id}` });
      c.m[p.id] = sig;
    });
  }, [project?.id, project?.pits, project?.pipes]); // eslint-disable-line react-hooks/exhaustive-deps
  const onMapClick = useCallback((ll) => addPointRef.current([ll.lat, ll.lng]), []);
  const armLogger = () => { if (!licensed()) return; net.setDraft(null); lg.setArm({}); toast.message(t.lgTapMap, { duration: 6000 }); };
  const onLoggerClick = useCallback((id) => lg.setSelId(id), []); // eslint-disable-line react-hooks/exhaustive-deps

  const cacheArea = () => cacheUrls(mapRef.current.tileJobs(2));
  useEffect(() => {
    const on = async (e) => {
      const n = await cacheUrls(mapRef.current?.routeTileJobs(e.detail.pts) || []);
      if (n) updateProject((p) => ({ ...p, plans: (p.plans || []).map((x) => (x.id === e.detail.id ? { ...x, offlineAt: Date.now(), offlineTiles: n } : x)) }));
    };
    window.addEventListener('giss-cache-route', on);
    return () => window.removeEventListener('giss-cache-route', on);
  });
  const cacheUrls = async (urls) => {
    setCacheProg(0);
    let ok = 0;
    try {
      const c = await caches.open('tiles-v1');
      for (let i = 0; i < urls.length; i += 6) {
        await Promise.all(urls.slice(i, i + 6).map(async (u) => {
          if (await c.match(u)) { ok++; return; }
          const res = await fetch(u, { mode: 'cors' });
          if (res.ok) { await c.put(u, res); ok++; }
        }).map((pr) => pr.catch(() => {})));
        setCacheProg(Math.round(((i + 6) / urls.length) * 100));
      }
      ok ? toast.success(t.cacheDone + ok) : toast.error(t.cacheErr);
    } catch { toast.error(t.cacheErr); }
    setCacheProg(null);
    return ok;
  };

  const selectProject = (id) => {
    net.setDraft(null);
    const p = projects.find((x) => x.id === id);
    setProjId(id);
    setRepId(p.reports[0].id);
    focusReport(p.reports[0]);
    localStorage.setItem('giss_last_work', id);
    setStartOpen(false);
  };
  // chooser: leisure keeps the previous work sector so the next work start restores it
  const WORK_KEY = 'giss_work_mode';
  const workMode = () => { if (getMode() !== 'outdoor') return; try { const w = JSON.parse(localStorage.getItem(WORK_KEY)); if (w?.m && w.m !== 'outdoor') applyProfiles([w.m], w.s); } catch { /* keep */ } };
  // user-started works: first choose the place (address / GPS / coordinates / recent), then create at the crosshair
  // project management (archive): one shared model, writes go through one dbPut per project
  const pmSave = async (np) => { const p = { ...np, updatedAt: Date.now() }; await dbPut(p); setProjects((prev) => (prev.some((x) => x.id === p.id) ? prev.map((x) => (x.id === p.id ? p : x)) : [p, ...prev])); if (archiveReady()) autoArchive(p); return p; };
  const loadReturn = async (raw) => {
    const ret = raw?.format === ENC ? await openEnvelope(raw, pmT()) : raw;
    if (!ret) return false;
    if (ret.format === PKG) { setPmPkg(ret); setPmOpen(true); return false; }
    const local = projects.find((x) => x.id === ret.projectId);
    let p;
    try { p = await applyReturn(local, ret); } catch { toast.error(pmT().retStale); return false; }
    await pmSave(p); toast.success(pmFill(pmT().retOk, { n: ret.rev })); return true;
  };
  const pmApp = {
    projects, current: project, mode: getMode(), lang, save: pmSave, importFile, loadReturn,
    open: (id, repId) => { setPmOpen(false); openWork(id); if (repId) setTimeout(() => setRepId(repId), 0); },
    map: (p) => { setPmOpen(false); openWork(p.id); setTimeout(() => mapRef.current?.setView(+p.lat, +p.lon, 17), 50); },
    pdf: (p, reps, o = {}) => exportPDF(p, reps, lang, { cover: reps.length > 1, ...o }),
    backup: (p) => exportJSON(p),
    create: async ({ name, customer, kind, branches, subs = [] }) => { const p = await createProject('project', name, true, true, null, { sector: branches[0], ...(subs[0] ? { sub: subs[0] } : {}), pm: { kind, branches, subs, customer, lead: localStorage.getItem('giss_pm_device') } }); return p; },
    demo: async () => { const [big, ...rest] = demoProjects(); big.pm.demoPkgs = await demoPackagesFor(big); for (const p of [big, ...rest]) await pmSave(p); },
  };
  useEffect(() => {
    const on = (e) => { const { posId, reportId } = e.detail; updateProject((p) => ({ ...p, pm: { ...p.pm, activePos: posId }, reports: p.reports.map((r) => (r.id === reportId ? { ...r, pm: { ...r.pm, posId, status: r.pm?.status === 'draft' ? 'open' : r.pm?.status } } : r)) })); };
    window.addEventListener('giss-pm-setpos', on);
    return () => window.removeEventListener('giss-pm-setpos', on);
  }, [updateProject]);
  const createHere = async (m, n = '') => { if (!licensed()) return false; setStartOpen(false); const pl = await pickPlace(m === 'single' ? 'single' : 'project'); if (!pl) return false; const r = await createProject(m, n, true, false, pl); if (r?.id) pl.later?.then((a) => { if (!a) return; setProjects((prev) => prev.map((p) => (p.id === r.id && !p.address ? { ...p, address: a, reports: p.reports.map((x, i) => (i === 0 && !x.address ? { ...x, address: a } : x)), updatedAt: Date.now() } : p))); scheduleSave(r.id); }); return r; };
  // first start: branch choice comes with the first project (setup -> start choice -> branch -> help)
  const startWork = (m, n) => { workMode(); return createHere(m, n); };
  const toFree = () => { if (getMode() !== 'outdoor') { localStorage.setItem(WORK_KEY, JSON.stringify({ m: getMode(), s: getSub() })); applyProfiles(['outdoor']); } };
  // works remember their sector: a tour opens in leisure mode, a work report in the work mode
  const openWork = (id) => { const p = projects.find((x) => x.id === id), sec = secOf(p); if (sec === 'outdoor') toFree(); else { workMode(); if (sec && getMode() !== 'all' && !inMode(p, getMode())) applyProfiles([sec]); } selectProject(id); };
  const startFree = async () => {
    toFree();
    openTrack();
    const today = projects.find((p) => p.sector === 'outdoor' && new Date(p.createdAt).toDateString() === new Date().toDateString());
    if (today) selectProject(today.id);
    else await createProject('project', '', true, true);
  };
  // every sector only shows its own works ('all' shows everything); legacy works without sector = all work sectors
  const inSec = useCallback((p) => { const m = getMode(); return m === 'all' || (p.sector ? inMode(p, m) : m !== 'outdoor'); }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (startOpen || !project || inSec(project)) return;
    const vis = projects.filter(inSec).sort((a, b) => b.updatedAt - a.updatedAt);
    if (vis[0]) selectProject(vis[0].id); else { localStorage.removeItem('giss_last_work'); createProject('project', '', false, true); }
  }, [mode, startOpen, project?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const continueLast = () => { const id = localStorage.getItem('giss_last_work'); const p = projects.find((x) => x.id === id); if (p && inSec(p)) openWork(id); else setStartOpen(false); };
  // fixed start preference (settings / chooser): skip the menu and go straight in
  const autoStart = () => {
    const pref = getStartPref();
    if (pref === 'free') return startFree();
    const last = projects.filter((p) => p.sector !== 'outdoor' && inSec(p) && (pref === 'single' ? p.mode === 'single' : p.mode !== 'single') && !(p.__fresh && p.updatedAt === p.createdAt)).sort((a, b) => b.updatedAt - a.updatedAt)[0];
    if (last) return openWork(last.id);
    workMode();
    return createProject(pref, '', true, true);
  };
  useEffect(() => { if (startOpen && stcReady && projects.length && startPref !== 'ask') Promise.resolve(autoStart()).finally(() => setStartOpen(false)); }, [stcReady, projects.length > 0]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const on = () => { setStartPref('ask'); setPrefState('ask'); setStcReady(false); setStartOpen(true); }; window.addEventListener('giss-setup', on); return () => window.removeEventListener('giss-setup', on); }, []);

  const selectReport = useCallback((id) => {
    setRepId(id);
    const r = projectsRef.current.find((p) => p.id === projId)?.reports.find((x) => x.id === id);
    focusReport(r);
  }, [projId]);

  const licensed = () => {
    if (hasModule('pro')) return true;
    toast.error(t.needLicense, { duration: 7000 }); setStartOpen(false); setShowSettings(true); return false;
  };

  // untouched auto-created / browse projects are thrown away when real work starts
  const dropJunk = () => { const junk = projects.filter((x) => x.__fresh && x.updatedAt === x.createdAt); junk.forEach((x) => dbDelete(x.id).catch(() => {})); return junk; };
  const nextColor = (list) => ZONE_COLORS.find((c) => !list.some((x) => x.color === c)) || ZONE_COLORS[list.length % ZONE_COLORS.length];
  const createProject = async (mode = 'project', name = '', persist = true, quiet = false, pl = null, extra = null) => {
    track('first_project');
    if (persist && !licensed()) return false;
    const p = newProject(lang, pl ? { lat: pl.lat, lng: pl.lon, zoom: pl.zoom } : mapRef.current?.getCenter(), mode, projects.filter((x) => x.mode === mode).length + 1);
    if (name) { p.title = name; if (mode === 'single') p.reports[0].title = name; }
    if (pl?.addr) { p.address = pl.addr; p.reports[0].address = pl.addr; }
    const junk = dropJunk(), rest = projects.filter((x) => !junk.includes(x));
    p.color = nextColor(rest);
    p.sector = getMode();
    if (p.sector === 'detect') p.sub = getSub();
    if (extra) Object.assign(p, extra);
    ensurePm(p);
    if (persist) await dbPut(p); else p.__fresh = true;
    setProjects([p, ...rest]);
    setProjId(p.id);
    setRepId(p.reports[0].id);
    localStorage.setItem('giss_last_work', p.id);
    setStartOpen(false);
    if (!persist || quiet) return p;
    toast.success(mode === 'single' ? t.btnNewSingle : t.btnNewProjectMode, { duration: 1800 });
    return p;
  };
  // AquaFlow demo project (white background or current map view)
  useEffect(() => {
    const on = async (e) => {
      const { kind, white, inp, place } = e.detail, c = window.__gissMapView?.()?.m?.getCenter() || { lat: 46.06, lng: 11.12 };
      let d;
      if (e.detail.net) d = e.detail.net;
      else if (inp || kind === 'net1' || kind === 'net3') {
        const txt = inp || await (await fetch(`/aqua-ref/${kind === 'net3' ? 'Net3' : 'Net1'}.inp`)).text();
        const r = inpToProject(txt, { mode: 'local', ...(place || {}), center: c }, t);
        d = { title: inp ? r.title : kind === 'net3' ? t.aqDemoNet3 : t.aqDemoNet1, description: [inp ? '' : kind === 'net3' ? t.aqDemoNet3Hint : t.aqDemoNet1Hint, r.miss.length ? `${t.aqImpMiss}: ${r.miss.join('; ')}` : ''].filter(Boolean).join('\n'), pipes: r.pipes, fittings: r.fittings, aqua: { hl: r.hl } };
      } else d = demoNet(kind, c, t);
      const mid = d.real && { lat: d.fittings.reduce((s, f) => s + f.lat, 0) / d.fittings.length, lon: d.fittings.reduce((s, f) => s + f.lon, 0) / d.fittings.length, zoom: 16 };
      const p = await createProject('project', d.title, true, true, mid || null, { description: d.description, pipes: d.pipes, fittings: d.fittings, blankMap: d.real ? false : white, ...(d.aqua ? { aqua: d.aqua } : {}), ...(d.importLog ? { importLog: d.importLog } : {}), ...(d.demo ? { demo: d.demo } : {}), ...(e.detail.cross ? { pm: { kind: 'cross', branches: ['detect', 'aqua'], subs: [getSub()] } } : {}) });
      if (!p) return;
      setTimeout(() => { if (d.fittings.length) mapRef.current?.fitAll(d.fittings.map((f) => [f.lat, f.lon]), window.innerWidth > 900 ? 470 : 60); window.dispatchEvent(new CustomEvent('giss-aqua-open', { detail: d.open || 'sim' })); }, 400);
      toast.success(inp ? t.aqImpDone : t.aqDemoDone);
    };
    window.addEventListener('giss-aqua-demo', on);
    return () => window.removeEventListener('giss-aqua-demo', on);
  });
  // leak detection: pipe network of another branch - used together (adds the detection branch) or copied into an own detection project
  useEffect(() => {
    const on = async (e) => {
      const { id, how } = e.detail, src = projects.find((x) => x.id === id), N = NST[lang] || NST.en;
      if (!src) return;
      const fit = () => setTimeout(() => src.fittings?.length && mapRef.current?.fitAll(src.fittings.map((f) => [f.lat, f.lon]), 60), 400);
      if (how === 'copy') {
        const c = JSON.parse(JSON.stringify({ pipes: src.pipes, fittings: src.fittings || [] })), pl = src.lat != null ? { lat: +src.lat, lon: +src.lon, zoom: 16 } : null;
        if (await createProject('project', `${src.title} – ${N.copySuffix}`, true, true, pl, { description: src.description || '', origin: src.id, ...c })) { fit(); toast.success(N.copied); }
        return;
      }
      if (how === 'share') {
        const pm = pmOf(src);
        await pmSave({ ...src, pm: logHist({ ...pm, kind: 'cross', branches: [...new Set([...(pm.branches || []), 'detect'])], subs: [...new Set([...(pm.subs || []), getSub()])] }, N.shared) });
        toast.success(N.shared);
      }
      selectProject(id); fit();
    };
    window.addEventListener('giss-net-take', on);
    return () => window.removeEventListener('giss-net-take', on);
  });
  // PC without GPS: Windows position coarser than 1 km -> offer to set the PC location once
  useEffect(() => {
    const on = (e) => { const x = PHT[lang] || PHT.en; toast.warning(x.warn.replace('{km}', Math.max(1, Math.round(e.detail / 1000))), { duration: 12000, action: { label: x.warnBtn, onClick: () => { setShowSettings(true); setTimeout(() => document.getElementById('pc-home')?.scrollIntoView({ block: 'center' }), 400); } } }); };
    window.addEventListener('giss-pc-home-warn', on);
    return () => window.removeEventListener('giss-pc-home-warn', on);
  }, [lang]);
  // correlated leak pin -> AquaFlow leak (same project when shared, original project of a copy) and simulate it
  useEffect(() => {
    const on = async (e) => {
      const N = NST[lang] || NST.en, src = project, leak = src?.fittings?.find((f) => f.id === e.detail?.id);
      if (!leak) return;
      const shared = inMode(src, 'aqua'), target = shared ? src : projects.find((x) => x.id === src.origin);
      if (!target) { toast.error(N.aqNoLink); return; }
      const f = aquaLeak(target, leak, shared);
      if (!f) { toast.error(N.aqNoPipe); return; }
      if (shared) await pmSave({ ...src, fittings: src.fittings.map((x) => (x.id === leak.id ? f : x)) });
      else {
        const again = leak.aquaId && (target.fittings || []).some((x) => x.id === leak.aquaId), g = again ? { ...f, id: leak.aquaId } : f;
        await pmSave({ ...target, fittings: again ? target.fittings.map((x) => (x.id === g.id ? g : x)) : [...(target.fittings || []), g] });
        await pmSave({ ...src, fittings: src.fittings.map((x) => (x.id === leak.id ? { ...x, aquaAt: g.aquaAt, aquaId: g.id } : x)) });
      }
      applyProfiles(['aqua']); selectProject(target.id);
      setTimeout(() => { window.dispatchEvent(new CustomEvent('giss-aqua-open', { detail: 'leak' })); window.dispatchEvent(new Event('giss-aqua-rerun')); }, 700);
      toast.success(N.aqDone);
    };
    window.addEventListener('giss-leak-aqua', on);
    return () => window.removeEventListener('giss-leak-aqua', on);
  });
  // AquaFlow roughness calibration: one patch for all pipes on the flow path
  useEffect(() => {
    const on = (e) => { const { ids, patch } = e.detail; const set = new Set(ids); updateProject((p) => ({ ...p, pipes: p.pipes.map((x) => (set.has(x.id) ? { ...x, ...patch(x) } : x)) })); };
    window.addEventListener('giss-pipes-patch', on);
    return () => window.removeEventListener('giss-pipes-patch', on);
  });

  const isProj = project?.mode !== 'single';
  const setProjectLoc = () => { const c = mapRef.current.getCenter(); updateProject((p) => ({ ...p, lat: c.lat, lon: c.lng })); toast.success(t.projLocSet); };

  const deleteProject = async () => {
    if (!project || !window.confirm(t.confirmDelProj)) return;
    await dbDelete(project.id);
    let rest = projects.filter((p) => p.id !== project.id);
    if (!rest.length) {
      const p = newProject(lang, mapRef.current?.getCenter());
      await dbPut(p);
      rest = [p];
    }
    setProjects(rest);
    setProjId(rest[0].id);
    setRepId(rest[0].reports[0].id);
    focusReport(rest[0].reports[0]);
  };

  const createReport = async () => {
    if (!licensed()) return;
    const c = await pickPlace('report');
    if (!c) return;
    const r = { ...newReport(lang, project.reports.length + 1, c.lat, c.lon, c.zoom), ...(c.addr ? { address: c.addr } : {}) };
    if (project.pm) r.pm = stampReport(project, getMode() === 'all' ? project.pm.branches[0] : getMode());
    updateProject((p) => ({ ...p, reports: [...p.reports, r], ...(mode === 'detect_inhouse' && !(p.plans || []).length ? { planAsk: false } : {}) }));
    setRepId(r.id);
    c.later?.then((a) => a && updateProject((p) => ({ ...p, reports: p.reports.map((x) => (x.id === r.id && !x.address ? { ...x, address: a } : x)) })));
  };

  const deleteReport = () => {
    if (project.reports.length <= 1) return toast.error(t.lastReport);
    if (!window.confirm(t.confirmDelRep)) return;
    const rest = project.reports.filter((r) => r.id !== report.id);
    updateProject((p) => ({ ...p, reports: rest }));
    setRepId(rest[0].id);
    focusReport(rest[0]);
  };

  const confirmPosition = () => {
    const c = mapRef.current.getCenter();
    updateReport({ lat: c.lat, lon: c.lng, zoom: c.zoom, locked: true });
    toast.success(t.confirmedMsg);
    if (!report.address) geoReverse(c.lat, c.lng, lang).then((a) => a && updateReport((r) => (r.address ? {} : { address: a })));
  };

  const unlockPosition = () => {
    focusReport(report);
    updateReport({ locked: false });
    toast(t.unlockedMsg);
  };

  const fitAll = () => mapRef.current.fitAll([...project.reports.map((r) => [r.lat, r.lon]), ...(isProj && project.lat != null ? [[project.lat, project.lon]] : []), ...(project.fittings || []).map((f) => [f.lat, f.lon]),
    ...(project.pipes || []).flatMap((p) => pipeLatLngs(p, project.fittings)), ...(project.pits || []).map((p) => objPos(p, 'pit', project.fittings)).filter(Boolean), ...lg.list.map((l) => [l.lat, l.lon])].filter((p) => p && p[0] != null));

  const gnssPlace = async (what) => {
    if (!gnssLive(gnssState())) return toast.error(t.gnssNoFix);
    if (what === 'line') {
      if (net.draftRef.current?.kind !== 'pipe') net.setDraft({ kind: 'pipe', type: 'water', dn: '', label: '', points: [] });
      return setGnssLine(true);
    }
    if (what === 'fit' && net.draftRef.current?.kind !== 'fitting') return toast.message(t.gnssPickFirst, { duration: 6000 });
    let g, draft = !extRtk();
    if (!draft) { const r = await measurePrecise(); if (!r) return; g = r.gnss; draft = r.draft; } else {
      const cfg = getGnssCfg();
      const tid = +cfg.avg > 1 ? toast.loading(`${t.gnssAveraging} (${cfg.avg})`) : null;
      g = gnssMeta(await averageGnss(+cfg.avg));
      if (tid) toast.dismiss(tid);
      toast.warning(t.prNoRtk, { id: 'prec-warn', duration: 8000 });
    }
    if (what === 'fit') net.addPoint([g.lat, g.lon], undefined, { gnss: g, ...(draft ? { draft: true } : {}) });
    else updateReport({ lat: g.lat, lon: g.lon, locked: true, gnss: { ...g, draft } });
    mapRef.current.setView(g.lat, g.lon);
    toast.success(what === 'fit' ? t.gnssPlacedFit : t.gnssPlacedRep);
  };

  const locateGPS = () => {
    const gs = gnssState();
    if (gnssLive(gs)) return mapRef.current.setView(gs.lat, gs.lon, 20);
    if (!navigator.geolocation) return toast.error(t.gpsErr);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude: la, longitude: lo } = pos.coords;
        mapRef.current.setView(la, lo, 19);
        if (!report.address) geoReverse(la, lo, lang).then((a) => a && setPicked({ lat: la, lon: lo, name: a, gps: true }));
      },
      () => toast.error(t.gpsErr),
      { enableHighAccuracy: true, timeout: 15000 },
    );
  };

  const triggerCamera = (role) => {
    if (!licensed()) return;
    roleRef.current = role;
    if (role === 'main' && isLocked(report)) focusReport(report);
    camRef.current.click();
  };
  const camFn = useRef(triggerCamera);
  camFn.current = triggerCamera;
  // agent actions: camera after the user's tap, values the user dictated, archive window
  useEffect(() => {
    const cam = () => camFn.current('main');
    const arc = () => { if (window.innerWidth >= 900) setPmOpen(true); };
    window.addEventListener('giss-agent-camera', cam);
    window.addEventListener('giss-archive-open', arc);
    return () => { window.removeEventListener('giss-agent-camera', cam); window.removeEventListener('giss-archive-open', arc); };
  }, []);
  useEffect(() => {
    const join = (a, b) => (a ? `${a}\n${b}` : b);
    const on = (e) => {
      const { field, value, set } = e.detail;
      if (field === 'report_title') updateReport({ title: value });
      else if (field === 'report_text') updateReport((r) => ({ text: set ? value : join(r.text, value) }));
      else if (field === 'project_title') updateProject((p) => ({ ...p, title: value }));
      else if (field === 'project_description') updateProject((p) => ({ ...p, description: set ? value : join(p.description, value) }));
    };
    window.addEventListener('giss-agent-fill', on);
    return () => window.removeEventListener('giss-agent-fill', on);
  }, [updateReport, updateProject]);

  const addFiles = async (files, role, extra = {}, live = false) => {
    if (!licensed()) return false;
    if (role === 'main') role = 'general';
    if (extra.lat == null) extra = { ...(await photoPos(report, live)), ...extra };
    const list = [];
    for (const f of files) {
      if (f.type.startsWith('video/')) {
        list.push({ id: uid('ph'), kind: 'video', role: 'general', blob: f, mime: f.type, caption: '', createdAt: Date.now(), ...extra, ...(await videoThumb(f)) });
        continue;
      }
      const img = await compressImage(f);
      const at = live ? {} : await placePhoto(f, project.tracks);
      list.push({ id: uid('ph'), kind: 'photo', role, caption: '', createdAt: Date.now(), ...extra, ...at, ...img });
    }
    if (role === 'main' && !list.some((x) => x.kind === 'photo')) role = 'general';
    updateReport((r) => {
      let photos = r.photos;
      if (role === 'main') photos = photos.map((p) => (p.role === 'main' ? { ...p, role: 'general' } : p));
      return { photos: role === 'main' ? [...list, ...photos] : [...photos, ...list], ...(role === 'main' ? { locked: false } : {}) };
    });
    return list.map((x) => x.id);
  };

  const onFileInput = async (e, role, live = false) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length) { const ids = await addFiles(files, role ?? roleRef.current, {}, live); if (editAfter.current && ids?.[0]) setTimeout(() => window.dispatchEvent(new CustomEvent('giss-photo-edit', { detail: ids[0] })), 50); }
    editAfter.current = false;
  };

  const setPhotoRole = (id, role) => updateReport((r) => ({
    photos: r.photos.map((p) => {
      if (p.id === id) return { ...p, role };
      if (role === 'main' && p.role === 'main') return { ...p, role: 'general' };
      return p;
    }),
    ...(role === 'main' ? { locked: false } : {}),
  }));
  const patchMedia = (rid, id, patch) => updateProject((p) => ({ ...p, reports: p.reports.map((r) => (r.id !== rid ? r : { ...r, photos: patch ? r.photos.map((m) => (m.id === id ? { ...m, ...patch } : m)) : r.photos.filter((m) => m.id !== id) })) }));
  const updatePhoto = (id, patch) => updateReport((r) => ({ photos: r.photos.map((p) => (p.id === id ? { ...p, ...patch } : p)) }));
  // photo branch: tap a photo pin -> its report (project: whole report with all reports) as PDF
  const openDocPin = async (id) => {
    const p = projects.find((x) => x.id === id); if (!p) return;
    toast.message(t.fxOpening, { id: 'doc-pin' });
    try { const { blob, name } = await photoReportPdf(p, t, lang); const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = name; a.click(); toast.success(name, { id: 'doc-pin' }); } catch { toast.error(t.fxPdfFail, { id: 'doc-pin' }); }
  };
  const deletePhoto = async (id) => (await askConfirm(t.confirmDelPhoto)) && updateReport((r) => ({ photos: r.photos.filter((p) => p.id !== id) }));

  const appendText = (txt) => txt && updateReport((r) => ({ text: (r.text ? r.text.replace(/\s+$/, '') + '\n' : '') + txt }));
  const onDictate = (prefix, transcript) => transcript && appendText(prefix + transcript);
  const addPhotoMemo = (id, i, m) => {
    updateReport((r) => ({
      photos: r.photos.map((p) => (p.id === id ? { ...p, memos: [...(p.memos || []), m], caption: p.caption || m.transcript } : p)),
      text: m.transcript ? (r.text ? r.text.replace(/\s+$/, '') + '\n' : '') + `[${t.photoWord} ${i + 1}] ${m.transcript}` : r.text,
    }));
  };
  const addReportMemo = (m) => updateReport((r) => ({
    memos: [...(r.memos || []), m],
    text: m.transcript ? (r.text ? r.text.replace(/\s+$/, '') + '\n' : '') + m.transcript : r.text,
  }));

  const standalone = window.matchMedia?.('(display-mode: standalone)').matches || navigator.standalone;
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const install = async () => {
    if (installEvt) { installEvt.prompt(); await installEvt.userChoice; setInstallEvt(null); } else toast.message(t.iosInstall, { duration: 8000 });
  };

  const openCounter = () => {
    if (!hasModule('inventory')) { toast.error(t.invLocked); setShowSettings(true); return; }
    invRef.current.click();
  };
  const onInvFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (f) setInvPhoto(await compressImage(f, 1600, 0.88));
  };
  const applyInventory = (photo, line, keep) => {
    updateReport((r) => ({
      photos: [...r.photos, { id: uid('ph'), kind: 'photo', role: 'detail', createdAt: Date.now(), ...photo }],
      text: (r.text ? r.text.replace(/\s+$/, '') + '\n' : '') + line,
    }));
    if (!keep) setInvPhoto(null);
  };
  const nextInvPhoto = async (f) => { setInvPhoto(await compressImage(f, 1600, 0.88)); setInvKey((k) => k + 1); };

  const run = async (key, fn) => {
    if (key !== 'json' && !licensed()) return;
    if (ft.network && ['pdf1', 'pdfAll', 'share1', 'shareAll', 'zip', 'dxf', 'geojson', 'kmz'].includes(key)) {
      const q = netQuality(project);
      if (!q.empty && q.issues.length && mode !== 'survey') toast.warning(t.nqWarn.replace('{s}', q.score).replace('{n}', q.issues.length), { duration: 8000, id: 'netq-warn', action: { label: t.nqShow, onClick: openNetQ } });
    }
    setBusy(key);
    try { await fn(); } catch (err) { console.error(err); toast.error(String(err.message || err)); }
    setBusy(null);
  };

  const importJSON = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (f) importFile(f);
  };
  importRef.current = importFile;
  // handover: keep the PC version before the field state replaces it (archive backup folder, else a local copy)
  async function backupOld(old) {
    const H = HO[lang] || HO.en;
    if (archiveReady() && await archiveBackup(old).catch(() => false)) { toast.success(H.backupArc); return; }
    const c = { ...old, id: uid('proj'), title: H.backupT.replace('{t}', old.title).replace('{d}', new Date(old.updatedAt || Date.now()).toLocaleString(t.locale)), backupOf: old.id };
    await dbPut(c);
    setProjects((prev) => [c, ...prev]);
    toast.success(H.backupLoc.replace('{t}', c.title));
  }
  async function importFile(f, quiet = false, fromArchive = false) {
    try {
      const raw = JSON.parse(await f.text());
      if (raw?.format === RET || raw?.format === ENC) return loadReturn(raw);
      if (raw?.format === PKG) { setPmPkg(raw); setPmOpen(true); return false; }
      const p = ensurePm(ensureProjectHasReports(await deserialize(raw), lang));
      if (p.agentDb) { mergeAgents(p.agentDb); delete p.agentDb; }
      if (!p.id || !p.title) throw new Error();
      const old = projects.find((x) => x.id === p.id);
      if (old) {
        if (hoBusy.current) { toast.error(t.importErr); return false; }
        hoBusy.current = true;
        const diff = fieldDiff(old, p), mode = await new Promise((res) => setHandover({ diff, res }));
        setHandover(null); hoBusy.current = false;
        if (mode === 'cancel') return false;
        if (mode === 'copy') { p.id = uid('proj'); p.title = (HO[lang] || HO.en).copyT.replace('{t}', p.title).replace('{d}', new Date(p.updatedAt || Date.now()).toLocaleString(t.locale)); }
        else if (diff.items.length) await backupOld(old);
        if (mode === 'take') toast.success((HO[lang] || HO.en).taken);
      }
      p.updatedAt = Date.now();
      localStorage.setItem(`giss_bak_${p.id}`, String(p.updatedAt));
      await dbPut(p);
      setProjects((prev) => [p, ...prev.filter((x) => x.id !== p.id)]);
      if (archiveReady() && !fromArchive) archiveProject(p, lang, null, 'phone').then(() => toast.success(t.arSaved.replace('{p}', p.title)), (e) => toast.error(t.arErr + (e?.message || e)));
      setProjId(p.id);
      setRepId(p.reports[0].id);
      focusReport(p.reports[0]);
      setStartOpen(false);
      if (!quiet) toast.success(t.importOk);
      return true;
    } catch { toast.error(t.importErr); return false; }
  }

  const BusyIcon = ({ k, icon }) => (busy === k ? <Loader2 size={16} className="spin" /> : icon);

  if (!project || !report) return <div className="loading"><Loader2 className="spin" /></div>;

  return (
    <div className={`app-container ${office && wide ? 'office' : ''}`} data-testid="app-root">
      {dragOn && <div className="drop-ov" data-testid="drop-overlay">📥 {t.trDrop}</div>}
      {handover && <HandoverDiff diff={handover.diff} onDone={handover.res} />}
      <Toaster position="top-center" richColors offset={{ top: toastTop }} mobileOffset={{ top: toastTop }} />
      {ft.tracks && <NaviBar t={t} plans={project?.plans} />}
      <ObjNavi t={t} project={project} />
      {agentRoute && (
        <div className="agent-route-card" data-testid="agent-route-card">
          <b>🗺 {agentRoute.name}</b>
          <small>{agentRoute.km != null && `${agentRoute.km.toFixed(1).replace('.', t.locale?.startsWith('en') ? '.' : ',')} km · ↑ ${Math.round(agentRoute.up || 0)} m`}{agentRoute.stops?.length ? ` · ${agentRoute.stops.length} ${t.planStopsN}` : ''}</small>
          <div className="row">
            <button className="btn btn-green btn-sm" data-testid="agent-route-take-btn" onClick={() => {
              const pl = { id: `plan_${Date.now()}`, name: agentRoute.name, pts: agentRoute.pts, mode: agentRoute.mode, km: agentRoute.km, up: agentRoute.up, stops: agentRoute.stops || [], steps: agentRoute.steps || [], elev: agentRoute.elev || [], t: Date.now() };
              updateProject((p) => ({ ...p, plans: [...(p.plans || []), pl] }));
              window.dispatchEvent(new CustomEvent('giss-agent-route', { detail: {} }));
              if (trackRec() && trackRec().status !== 'done') { toast.message(t.agentRouteBusy, { duration: 8000 }); return; }
              setTimeout(() => window.dispatchEvent(new CustomEvent('giss-track-open', { detail: { ref: pl.id, profile: agentRoute.mode === 'foot' ? 'hike' : 'road' } })), 50);
              toast.success(t.agentRouteSaved);
            }}>{t.agentRouteTake}</button>
            <button className="btn btn-outline btn-sm" data-testid="agent-route-drop-btn" onClick={() => window.dispatchEvent(new CustomEvent('giss-agent-route', { detail: {} }))}>{t.agentRouteDrop}</button>
          </div>
        </div>
      )}
      {startOpen && stcReady && (projects.length > 0 || needsOnboarding()) && startPref === 'ask' && <StartChooser t={t} projects={projects} onCreate={startWork} onOpen={openWork} onClose={continueLast} onBrowse={() => createProject('project', '', false)} onFree={startFree} />}
      <TourGuide t={t} lang={lang} />
      <HintSpeaker t={t} lang={lang} />
      <SetupGuide t={t} />
      <CalibReminder t={t} />
      {project && <DepthPrompt t={t} project={project} updateProject={updateProject} />}
      <DemoMode t={t} lang={lang} />
      <FeatureTree lang={lang} />
      <ScreenDimmer t={t} />
      {flyTrack && <TourFlyover t={t} track={flyTrack} onClose={() => setFlyTrack(null)} />}
      <header className="top-bar">
        <div className="brand">
          <img className="brand-logo" src="/icon-192.png" alt="GISS" data-testid="brand-logo" />
          <div>
            <h1 data-testid="app-title">{t.appTitle}</h1>
            <p>{APP_INFO.publisher} · {t.appSub}</p>
          </div>
        </div>
        <span className={`net-badge ${online ? 'on' : 'off'}`} data-testid="network-status">
          {online ? <Wifi size={13} /> : <WifiOff size={13} />} {online ? t.online : t.offline}
        </span>
        {!standalone && (installEvt || isIOS) && (
          <button className="chip install-chip" data-testid="install-app-btn" onClick={install}><Download size={13} /> {wide ? `💻 ${t.btnInstallPc}` : t.btnInstall}</button>
        )}
        {(ft.network || ft.fittings || ft.garden || ft.facade || ft.inventory) && <button className="chip help-chip cat-chip" data-testid="catalog-open-btn" onClick={() => setShowCatalog(true)} title={t.catTitle}><Boxes size={15} /> <span>{t.catOpen}</span></button>}
        {(ft.network || ft.fittings) && mode !== 'garden' && <button className="chip help-chip cat-chip" data-testid="mfr-open-btn" onClick={openMfr} title={t.mfTitle}>🏭 <span>{t.mfOpen}</span></button>}
        {(ft.network || ft.fittings) && !mode.startsWith('detect') && mode !== 'garden' && <button className="chip help-chip cat-chip" data-testid="pond-open-btn" onClick={openPond} title={t.fpOpen}>📐 <span>{t.fpOpen}</span></button>}
        <HelpMenu t={t} manualLabel={MANUAL[lang]?.open} onManual={() => setShowManual(true)} onStartHelp={() => { resetSetup(); restartTour(); toast.success(t.startHelpDone); }} onTree={openTree} />
        <AgentButton t={t} lang={lang} project={project} />
        <UiScaleBtn t={t} />
        <GloveBtn t={t} />
        {wide && <button className="chip transfer-chip" data-testid="archive-btn" onClick={() => setPmOpen(true)} title={t.arTitle}>🗄️ <span>{t.arBtn}</span></button>}
        <button className="chip transfer-chip" data-testid="transfer-btn" onClick={() => setShowTransfer(true)} title={t.trTitle}>🔄 <span>{t.trBtn}</span></button>
        {wide && <button className={`chip office-chip ${office ? 'on-chip' : ''}`} data-testid="office-mode-btn" aria-pressed={office} onClick={() => setOffice(!office)} title={t.officeTitle}>🖥️ <span>{t.officeBtn}</span></button>}
        <button className="chip share-chip" data-testid="app-share-btn" onClick={() => setShowShare(true)} title={t.appShareTitle}>📲 <span>{t.shBtn}</span></button>
        <button className="chip settings-chip" data-testid="settings-btn" onClick={() => setShowSettings(true)} aria-label={t.settings}><Settings size={15} /><span className="big-only">{t.settings}</span></button>
        <LangSelect t={t} lang={lang} setLang={setLang} />
      </header>
      <div className="mode-bar-wrap"><ModeBar t={t} />{ft.gnss && <RtkBadge t={t} />}</div>
      <PrecisionDialog t={t} />
      <ShutPanel t={t} project={project} updateProject={updateProject} />
      <EscCloser />
      <PlacePicker t={t} lang={lang} mapRef={mapRef} />
      {project && <NetQualityDialog t={t} project={project} />}
      {project && ft.aqua && <AquaPanel t={t} project={project} updateProject={updateProject} />}
      {ft.aqua && <Aqua3D t={t} />}
      {project && mode === 'garden' && <IrrBomDialog t={t} project={project} updateProject={updateProject} />}
      <FeedbackButton t={t} />
      {project && ['detect_inhouse', 'detect_roof'].includes(mode) && <FloorPlans t={t} project={project} updateProject={updateProject} />}
      {project && ['detect_inhouse', 'detect_roof'].includes(mode) && <SketchEditor t={t} project={project} updateProject={updateProject} />}
      {project && ['detect_inhouse', 'detect_roof', 'building'].includes(mode) && <MoistGrid t={t} project={project} updateProject={updateProject} />}
      {project && mode === 'detect_inhouse' && <Pano360 t={t} project={project} updateProject={updateProject} />}
      {project && ['detect_inhouse', 'detect_roof', 'detect_heat', 'photo'].includes(mode) && <SitePlans t={t} project={project} updateProject={updateProject} mapRef={mapRef} />}
      {project && !startOpen && <BackupReminder t={t} project={project} />}
      {project && mode === 'detect_roof' && <RoofFrame t={t} project={project} updateProject={updateProject} />}
      {project && ['detect_inhouse', 'detect_roof'].includes(mode) && !startOpen && !project.__fresh && <PlanStart t={t} project={project} updateProject={updateProject} />}
      {project && ft.survey && <CoordList t={t} project={project} updateProject={updateProject} />}
      {project && mode === 'garden' && <IrrPlan t={t} project={project} updateProject={updateProject} />}
      {project && mode === 'garden' && <AutoPlaceBar t={t} project={project} updateProject={updateProject} />}
      <AreaEditBar t={t} />
      {sel?.kind === 'pit' && (project?.pits || []).some((x) => x.id === sel.id) && <PitEditBar t={t} pit={project.pits.find((x) => x.id === sel.id)} patch={pits.patch} onClose={() => setSel(null)} onDuplicate={(cp) => { const id = uid('pit'); updateProject((p) => ({ ...p, pits: [...(p.pits || []), { ...cp, id, name: copyName(cp.name, t), media: [], memos: [], maint: undefined, maintLog: [] }] })); setSel({ kind: 'pit', id }); toast.success(t.aeDupOk); }} />}
      {mode === 'garden' && <AgentDb t={t} />}
      <StartLinkPrompt t={t} />
      {project && ft.kks && <KksBanner t={t} project={project} onOpen={openFit} />}
      {project && ft.scaffold && <ScaffoldBanner t={t} project={project} />}
      {project && ft.kks && <KksPlan t={t} project={project} mapRef={mapRef} onOpen={openFit} />}
      {ft.xbear && project && <CrossBearing t={t} mapApi={mapRef} project={project} updateProject={updateProject} repId={report?.id} />}
      <ExpiryBanner t={t} onOpen={() => setShowSettings(true)} />
      <BlockedDialog t={t} onClose={() => setLicTick((n) => n + 1)} />
      {pits.editing && <PitDialog key={pits.editing.id} t={t} pits={pits} />}
      <LeakPhotoPrompt t={t} net={net} fittings={project.fittings} />
      <LoggerDialog key={lg.dlg ? lg.dlg.pt.join() : "none"} t={t} lg={lg} />
      {project && <SurveyDialog t={t} project={project} net={net} mapApi={mapRef} />}
      <VisMenu t={t} />
      <BoundaryPrompt t={t} bnd={bnd} onClose={() => setBnd(null)}
        onUnknown={() => { setPrivMode(true); setBnd(null); toast.message(t.privModeHint, { duration: 7000 }); }}
        onMeasured={() => { net.setDraft({ kind: 'pipe', type: bnd.type || 'water', color: bnd.color, dn: '', label: t.privPipe, private: true, points: [{ fid: bnd.fid }] }); setBnd(null); toast.message(t.privHint, { duration: 9000 }); }} />
      <PrivModeBar t={t} on={privMode} onEnd={() => setPrivMode(false)} />
      <LoggerSheet t={t} lg={lg} />
      <GnssDialog t={t} center={() => mapRef.current.getCenter()} onPlace={gnssPlace} project={project} updateProject={updateProject} />
      <StreetViewModal t={t} net={net} project={project} onPlaced={(pt) => mapRef.current.fitAll([pt])}
        renderSheet={(s, close) => <ObjectSheet t={t} sel={s} project={project} updateProject={updateProject} net={net} pits={pits} onMedia={patchMedia} onClose={close} />} />
      {showPrices && project && <ProjectPrices t={t} project={project} updateProject={updateProject} onClose={() => setShowPrices(false)} />}
      {showCatalog && <CatalogDialog t={t} onClose={() => setShowCatalog(false)} />}
      <MfrDialog t={t} wide />
      <FirePondPlanner t={t} wide project={project} report={report} updateReport={updateReport} />
      <NetShare lang={lang} projects={projects} current={project} />
      {showManual && <ManualDialog lang={lang} focus={helpKey} onClose={() => { setShowManual(false); setHelpKey(null); }} />}
      {showArchive && <ArchiveDialog t={t} lang={lang} project={project} projects={projects} onImport={importFile} onClose={() => setShowArchive(false)} />}
      {pmOpen && <ProjectManager t={t} app={pmApp} initPkg={pmPkg} onClose={() => { setPmOpen(false); setPmPkg(null); }} onFolder={() => { setPmOpen(false); setShowArchive(true); }} />}
      <AgentPointer t={t} />
      <AgentSteps t={t} />
      {showTransfer && <TransferDialog t={t} project={project} onImport={importFile} onClose={() => setShowTransfer(false)} />}
      {showHandoff && <TourHandoff t={t} project={project} importFile={importFile} onClose={() => setShowHandoff(false)} />}
      <TrackSheet t={t} plots={project.reports || []} tracks={project.tracks || []} routeNo={(project.tracks || []).length + 1} refs={[...((project.pipes || []).length ? [{ id: 'pipes', name: `🛠 ${t.devPipes}`, lines: project.pipes.map((p) => pipeLatLngs(p, project.fittings)) }] : []), ...(project.plans || []).map((pl) => ({ id: pl.id, name: `🗺 ${pl.name}`, lines: [pl.pts] })), ...(project.tracks || []).map((tr) => ({ id: tr.id, name: `🧭 ${tr.name}`, lines: [tr.pts.map((p) => [p.lat, p.lon])] }))]} onDiscard={(ids) => updateProject((p) => ({ ...p, wpMedia: (p.wpMedia || []).filter((m) => !ids.includes(m.id)) }))} onSave={(tr) => updateProject((p) => ({ ...p, tracks: [...(p.tracks || []), tr] }))} />
      <WaypointFab t={t} mapApi={mapRef} onPoint={(w) => {
        if (w.kind !== 'leak') {
          if (w.media.length || w.memos.length) updateProject((p) => ({ ...p, wpMedia: [...(p.wpMedia || []), { id: w.id, ref: w.ref, kind: w.kind, media: w.media, memos: w.memos }] }));
          return null;
        }
        const fid = uid('fit');
        const f = { id: fid, type: 'leak', state: 'defect', severity: 'medium', name: `${t.fit_leak} ${w.ref}`, note: w.note, lat: w.lat, lon: w.lon, media: w.media, memos: w.memos, reportId: report?.id, trackRef: w.ref, ...(w.alt != null ? { elev: { v: +(+w.alt).toFixed(2), src: w.altSrc || 'gps', acc: null, at: w.t } } : {}) };
        updateProject((p) => ({ ...p, fittings: [...(p.fittings || []), f] }));
        return fid;
      }} />
      {showShare && <AppShare t={t} lang={lang} onClose={() => setShowShare(false)} />}

      <div className="work-bar" data-testid="work-bar">
        <select data-testid="project-select" value={project.id} onChange={(e) => selectProject(e.target.value)} aria-label={t.workLabel}>
          {projects.filter((p) => p.id === project.id || inSec(p)).map((p) => <option key={p.id} value={p.id}>{leisure ? '🥾' : p.mode === 'single' ? `[${t.modeSingle}]` : `[${t.modeProject}]`} {relabel(p.title) || '—'}{p.mode !== 'single' ? ` (${p.reports.length})` : ''}</option>)}
        </select>
        {!leisure && <button className="btn btn-single btn-sm" data-testid="new-single-btn" onClick={() => createHere('single')}><FilePlus2 size={15} /> {t.btnNewSingle}</button>}
        {!leisure && <button className="btn btn-proj btn-sm" data-testid="new-project-btn" onClick={() => createHere('project')}><FolderKanban size={15} /> {t.btnNewProjectMode}</button>}
        <button className="icon-btn danger" data-testid="delete-project-btn" onClick={deleteProject}><Trash2 size={16} /></button>
      </div>
      <div className="status-msg" data-testid="save-status">
        {saveState.saving ? <><Loader2 size={12} className="spin" /> {t.saving}</> : <><Check size={12} /> {t.savedMsg}{(saveState.at || new Date(project.updatedAt)).toLocaleTimeString(t.locale)}</>}
      </div>

      {isProj && (
        <section className="panel panel-project" data-testid="project-panel">
          <div className="banner banner-project" data-testid="project-banner">
            <div><small>{t.bnProject}</small><strong>{project.title || '—'}</strong></div>
            <span className="banner-brand">{APP_INFO.publisher}</span>
          </div>
          <label className="panel-label"><FolderOpen size={15} /> {t.projLabel}</label>
          <DictateField t={t} testid="project-title-input" placeholder={t.projPlaceholder} value={project.title} onChange={(v) => updateProject((p) => ({ ...p, title: v }))} />
          <div className="proj-desc">
            <label className="panel-label">{t.projDescTitle}</label>
            <DictateField t={t} as="textarea" testid="project-desc-input" placeholder={leisure ? t.tourDescPh : t.projDescPlaceholder} value={project.description || ''} onChange={(v) => updateProject((p) => ({ ...p, description: v }))} />
          </div>
          {!leisure && <div className="row">
            <button className="btn btn-proj btn-sm" data-testid="project-location-btn" onClick={setProjectLoc}><Flag size={14} /> {t.projLocBtn}</button>
            <span className="status-msg" data-testid="project-location">{project.lat?.toFixed(6)}, {project.lon?.toFixed(6)}</span>
            {ft.route && pv('dayRoute') && <button className="btn btn-green btn-sm" data-testid="route-planner-btn" onClick={() => setShowRoute(true)}><Route size={14} /> {t.routeDayBtn}</button>}
            {ft.lv && ft.costs && pv('prices') && <button className="btn btn-dark btn-sm" data-testid="project-prices-btn" onClick={() => setShowPrices(true)}><Tags size={14} /> {t.ppTitle}{countProjectPrices(project) > 0 && <em className="count" data-testid="project-prices-count">{countProjectPrices(project)}</em>}</button>}
          </div>}
          {!leisure && pv('roadPrefetch') && <RoadPrefetch t={t} project={project} />}
          {!leisure && pv('projAddr') && <div className="addr-line"><label className="addr-field"><MapPin size={14} /><input type="text" data-testid="project-address-input" placeholder={t.addrLabel} value={project.address || ''} onChange={(e) => updateProject((p) => ({ ...p, address: e.target.value }))} /></label>{project.lat != null && <a className="btn btn-green btn-sm addr-route" data-testid="project-route-btn" target="_blank" rel="noreferrer" href={`https://www.google.com/maps/dir/?api=1&destination=${project.lat},${project.lon}&travelmode=driving`} title={t.routeTitle}><Navigation size={14} /> {t.routeBtn}</a>}</div>}
        </section>
      )}

      {project && ft.maint && <MaintStats t={t} project={project} updateProject={updateProject} />}
      {project && ft.loggers && <LoggerRoutes t={t} project={project} compact updateProject={updateProject} />}
      {project && ft.pits && <AlarmCard t={t} project={project} onLocate={(pt) => { document.querySelector('[data-testid="map-wrapper"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' }); mapRef.current.fitAll([pt]); }} />}
      {project && ft.maint && <OverdueCard t={t} fittings={project.fittings} onLocate={(f) => { document.querySelector('[data-testid="map-wrapper"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' }); mapRef.current.fitAll([[f.lat, f.lon]]); }} />}
      <section className="panel panel-report">
        {!leisure && <div className="banner banner-report" data-testid="report-banner">
          <div className="banner-num"><small>{isProj ? t.bnPoint : t.modeSingle}</small><b data-testid="report-banner-num">{isProj ? `#${project.reports.indexOf(report) + 1}` : t.singleBadge}</b></div>
          <div className="banner-main"><small>{t.bnReport}</small><strong>{report.title || '—'}</strong>{waitMap?.[report.id] && (() => { const w = waitState(project.tracks, report.id); return <em className={`wait-badge wait-${w.st}`} data-testid="wait-badge">{w.st === 'green' ? `🟢 ${t.wtFree}` : `${w.st === 'red' ? '🔴' : '🟡'} ${t.wtFree}`} {new Date(w.end).toLocaleDateString(t.locale)}</em>; })()}</div>
          <div className="banner-right">
            <span className="banner-brand">{APP_INFO.publisher}</span>
            <span>{new Date(report.updatedAt || Date.now()).toLocaleString(t.locale)}</span>
            <span>{report.lat.toFixed(6)}, {report.lon.toFixed(6)}</span>
          </div>
        </div>}
        <label className="panel-label"><MapPinned size={15} /> {t.repLabel} <HelpHere t={t} area="report" className="chip help-chip" /></label>
        <div className="row">
          <select data-testid="report-select" value={report.id} onChange={(e) => selectReport(e.target.value)}>
            {project.reports.map((r, i) => <option key={r.id} value={r.id}>{`${i + 1}. ${r.title || '—'}`}</option>)}
          </select>
          {isProj ? (
            <>
              <button className="btn btn-accent btn-sm" data-testid="new-report-btn" onClick={createReport}><Plus size={15} /> {t.btnNewRep}</button>
              <button className="icon-btn danger" data-testid="delete-report-btn" onClick={deleteReport}><Trash2 size={16} /></button>
            </>
          ) : mode !== 'outdoor' && (
            <button className="btn btn-proj btn-sm" data-testid="convert-to-project-btn" onClick={() => updateProject((p) => ({ ...p, mode: 'project', lat: report.lat, lon: report.lon }))}><FolderKanban size={15} /> {t.convertToProject}</button>
          )}
        </div>
        <DictateField t={t} testid="report-title-input" placeholder={t.repPlaceholder} value={report.title} onChange={(v) => updateReport({ title: v })} />
        {project.pm && report.pm && <PosBar t={t} project={project} report={report} onProject={updateProject} onReport={updateReport} />}
        {!leisure && pv('repAddr') && <div className="addr-line"><label className="addr-field"><MapPin size={14} /><input type="text" data-testid="report-address-input" placeholder={t.addrLabel} value={report.address || ''} onChange={(e) => updateReport({ address: e.target.value })} /></label><a className="btn btn-green btn-sm addr-route" data-testid="report-route-btn" target="_blank" rel="noreferrer" href={`https://www.google.com/maps/dir/?api=1&destination=${report.lat},${report.lon}&travelmode=driving`} title={t.routeTitle}><Navigation size={14} /> {t.routeBtn}</a></div>}
        {isProj && !slim && pv('done') && <label className={`done-toggle ${report.done ? 'on' : ''}`} data-testid="report-done-toggle"><input type="checkbox" data-testid="report-done-checkbox" checked={!!report.done} onChange={(e) => updateReport({ done: e.target.checked })} /><CircleCheck size={15} /> {t.repDone}</label>}
        {!slim && <WorkLog t={t} report={report} updateReport={updateReport} show={pv} />}
      </section>

      <h3 className="sec"><span>01</span>{t.mapSectionTitle}<HelpBtn k="n:quick_map" testid="sec01-help" title={t.helpHere} /></h3>
      <p className="hint">{t.mapHint}</p>
      <div className="row">
        <button className="btn btn-primary" data-testid="take-main-photo-btn" onClick={() => triggerCamera('general')}><Camera size={16} /> {t.fsPhoto}</button>
        {extOk('tiles') && <button className="btn btn-outline btn-sm" data-testid="cache-area-btn" disabled={cacheProg !== null || !online} onClick={cacheArea}>
          {cacheProg !== null ? <><Loader2 size={16} className="spin" /> {Math.min(cacheProg, 100)}%</> : <><CloudDownload size={16} /> {t.btnCacheArea}</>}
        </button>}
      </div>

      <AddressSearch t={t} lang={lang} online={online} picked={picked} setPicked={setPicked} onGo={(lat, lon) => mapRef.current.setView(lat, lon, 19)}>
        <button className="btn btn-primary btn-sm" data-testid="address-set-report-btn" onClick={() => { const c = mapRef.current.getCenter(); updateReport({ lat: c.lat, lon: c.lng, zoom: c.zoom, ...(picked?.coords ? {} : { address: picked?.name }) }); toast.success(t.addrRepSet); }}><MapPinned size={15} /> {t.addrSetReport}</button>
        {isProj && <button className="btn btn-proj btn-sm" data-testid="address-set-project-btn" onClick={() => { setProjectLoc(); if (picked && !picked.coords) updateProject((p) => ({ ...p, address: picked.name })); }}><Flag size={15} /> {t.addrSetProject}</button>}
      </AddressSearch>
      <MapView waitMap={waitMap}
        ref={mapRef}
        projectPin={isProj && project.lat != null ? { lat: project.lat, lon: project.lon, name: project.title, color: project.color || ZONE_COLORS[Math.max(0, projects.indexOf(project)) % ZONE_COLORS.length] } : null}
        t={t}
        reports={project.reports}
        pipes={project.planOn && ['detect_inhouse', 'detect_roof'].includes(mode) ? project.pipes.filter((x) => onFloor(project, x)) : project.pipes}
        fittings={project.planOn && ['detect_inhouse', 'detect_roof'].includes(mode) ? project.fittings.filter((x) => onFloor(project, x)) : mode === 'aqua' ? scenFits(project) : project.fittings}
        floorPlan={['detect_inhouse', 'detect_roof'].includes(mode) ? activePlan(project) : null}
        sites={['detect_inhouse', 'photo'].includes(mode) ? project.sites : null}
        docPins={mode === 'photo' ? docPinsOf(projects.filter((p) => p.id !== project?.id)) : null}
        onDocPin={openDocPin}
        floorBar={['detect_inhouse', 'detect_roof'].includes(mode) ? <FloorBar t={t} project={project} updateProject={updateProject} /> : null}
        draft={net.draft}
        onMapClick={(ll) => { if (!net.draftRef.current) setSel(null); onMapClick(ll); }}
        netUnlocked={net.netUnlocked}
        onFittingClick={(id) => { const lf = lg.armRef.current && project.fittings.find((x) => x.id === id); if (lf) return onMapClick({ lat: lf.lat, lng: lf.lon }); return net.pickFit(id) || (net.draftRef.current ? net.onFittingClick(id) : setSel({ kind: 'fitting', id })); }}
        selId={sel?.id}
        onPipeClick={(id, ll) => (lg.armRef.current && ll ? onMapClick(ll) : setSel({ kind: 'pipe', id }))}
        onMovePipe={(id, points) => net.updatePipe(id, { points })}
        onPitChange={pits.patch}
        setDraft={net.setDraft}
        onMediaClick={(repId, id) => setSel({ kind: 'media', repId, id })}
        onMoveMedia={(repId, id, lat, lon) => patchMedia(repId, id, { lat, lon })}
        onMoveFitting={net.moveFitting}
        onMoveVertex={net.moveVertex}
        onNetConfirm={() => net.setNetLocked(true)}
        fitFilter={fitFilter}
        onLineClick={(id, ll) => (lg.armRef.current ? onMapClick(ll) : net.insertVertex(id, ll))}
        onDeleteVertex={net.deleteVertex}
        activeId={report.id}
        report={report}
        onSelect={onPinClick}
        onConfirm={confirmPosition}
        onUnlock={unlockPosition}
        overlayVisible={overlayVisible}
        onToggleOverlay={() => setOverlayVisible((v) => !v)}
        onPhoto={() => triggerCamera('main')}
        onGallery={() => licensed() && mainGalRef.current.click()}
        onXray={() => { if (!licensed()) return; if (!fs) toggleFs(true); setCamReq({ mode: 'photo', xray: true, n: Date.now() }); }}
        onOverlayChange={updateReport}
        onGnssPlace={gnssPlace}
        onFitAll={fitAll}
        onGps={locateGPS}
        gnssLineOn={gnssLine}
        loggers={lg.list}
        loggerPlan={project.loggerPlan}
        noise={noise}
        tracks={project.tracks}
        cad={project.cad}
        blank={!!project.blankMap}
        pressureCfg={project.pressure}
        gardens={project.gardens}
        dryStrict={!!project.dryStrict}
        irrProject={project}
        drawings={project.drawings}
        faults={project.faults}
        onCadLayer={(k) => updateProject((p) => ({ ...p, cad: { ...p.cad, off: (p.cad.off || []).includes(k) ? p.cad.off.filter((x) => x !== k) : [...(p.cad.off || []), k] } }))}
        loggerSel={lg.selId}
        onLoggerClick={onLoggerClick}
        lastId={net.lastFit?.id || project.fittings?.at(-1)?.id || project.pipes?.at(-1)?.id}
        fullscreen={fs}
        onToggleFullscreen={() => toggleFs(!fs)}
        follow={follow}
        pits={pits.pits}
        onPitClick={(id) => setSel({ kind: 'pit', id })}
      >
        <ProfilePicker t={t} />
        <PhotoDraw t={t} report={report} onSave={(photo) => updateReport((r) => ({ photos: [...r.photos, { id: uid('ph'), kind: 'photo', role: 'detail', createdAt: Date.now(), ...photo }] }))} />
        {project && <DrawStudio t={t} project={project} updateProject={updateProject} mapRef={mapRef} />}
        {project && ft.facade && <FacadeStudio t={t} project={project} updateProject={updateProject} repId={net.repId} />}
        {project && ft.corr && <CorrCalc t={t} project={project} updateProject={updateProject} mapRef={mapRef} />}
        {project && ft.fault && <CableFault t={t} project={project} updateProject={updateProject} mapRef={mapRef} />}
        {project && <StakeOut t={t} project={project} updateProject={updateProject} mapRef={mapRef} />}
        {project && <LoggerPlan t={t} lang={lang} project={project} updateProject={updateProject} repId={report?.id} mapRef={mapRef} />}
        <RoundImport t={t} project={project} updateProject={updateProject} />
        {project && ft.garden && <GardenCalc t={t} project={project} updateProject={updateProject} pits={pits} />}
        {sel && <ObjectSheet t={t} sel={sel} project={project} updateProject={updateProject} net={net} pits={pits} onMedia={patchMedia} onClose={() => setSel(null)} />}
        {fs && <FieldMode t={t} net={net} pits={pits} mapRef={mapRef} onGps={locateGPS} onCapture={(f, pos) => addFiles([f], 'general', pos)} follow={follow} setFollow={setFollow} camReq={camReq} onMainPhoto={() => triggerCamera('main')} onGallery={() => licensed() && mainGalRef.current.click()} onLogger={armLogger} onFitAll={fitAll} />}
        {!fs && wide && <ToolDrawer t={t} net={net} pits={pits} onCamera={(m) => { if (!licensed()) return; toggleFs(true); setCamReq({ mode: m, n: Date.now() }); }} onGps={locateGPS} onMemo={() => toggleFs(true)} onMainPhoto={() => triggerCamera('main')} onGallery={() => licensed() && mainGalRef.current.click()} onLogger={armLogger} onFitAll={fitAll} />}
        {gnssLine && <GnssLineBar t={t} net={net} onClose={() => setGnssLine(false)} />}
        {pdCam && <GhostCamera t={t} mode="photo" op0={100} review={false} onShot={async (f) => { setPdCam(false); setPdBusy(true); toast.loading(t.camSaved, { id: 'pd-shot' }); const ids = await addFiles([f], 'general', {}, true); setPdBusy(false); toast.success(t.camSaved, { id: 'pd-shot', duration: 1500 }); if (ids?.[0]) setTimeout(() => window.dispatchEvent(new CustomEvent('giss-photo-edit', { detail: ids[0] })), 50); }} onClose={() => setPdCam(false)} onFail={() => { setPdCam(false); editAfter.current = true; triggerCamera('general'); }} />}
        {mode === 'photo' && report && !pdCam && (
          <div className={`pd-fabs ${fs ? 'in-fs' : ''}`} data-testid="photo-fabs">
            <button type="button" className="pd-fab cam" data-testid="photo-fab-camera" aria-label={t.fxFabPhoto} title={t.fxFabPhoto} disabled={pdBusy} onClick={() => licensed() && (navigator.mediaDevices?.getUserMedia ? setPdCam(true) : (editAfter.current = true, triggerCamera('general')))}>{pdBusy ? <Loader2 size={30} className="spin" /> : <Camera size={30} />}</button>
            <span className="pd-fab-mic" title={t.fxFabMic}><VoiceRecorder t={t} testid="photo-fab-mic" onDone={(m) => { const ph = (report.photos || []).filter((p) => p.kind !== 'video'), last = ph[ph.length - 1]; if (last) addPhotoMemo(last.id, report.photos.indexOf(last), m); else addReportMemo(m); }} /></span>
          </div>
        )}
      </MapView>

      {mainPhotoOf(report) && <OverlayControls t={t} report={report} onChange={updateReport} />}

      {(ft.network || ft.fittings || ft.pits || ft.tracks) && <h3 className="sec" data-testid="sec02-title"><span>02</span>{ml(t, 'pipesTitle', mode)} {ft.network && <em className="count" data-testid="pipe-count">{project.pipes.length}</em>}<HelpBtn k="n:quick_draw" testid="sec02-help" title={t.helpHere} /></h3>}
      {ft.tracks && <TrackList t={t} project={project} updateProject={updateProject} onLocate={(pts) => mapRef.current.fitAll(pts)} />}
      {(ft.network || ft.fittings || ft.pits) && <PipePanel t={t} project={project} net={net} pits={pits} fs={fs} onDictate={onDictate} fitFilter={fitFilter} setFitFilter={setFitFilter} onLocate={(pts) => mapRef.current.fitAll(pts)} />}

      {ft.scaffold && <ScaffoldRent t={t} project={project} updateProject={updateProject} />}
      {ft.costs && !slim && pv('daily') && <DailyReport t={t} report={report} updateReport={updateReport} />}
      <h3 className="sec"><span>03</span>{t.photosTitle} <em className="count" data-testid="photo-count">{report.photos.length}</em><HelpBtn k="n:quick_photo" testid="sec03-help" title={t.helpHere} /><button className="btn btn-proj btn-sm sec-act" data-testid="photodraw-open-btn" onClick={() => openPhotoDraw()}>🖍️ {t.pdShort}</button></h3>
      <div className="row">
        {mode !== 'outdoor' && <button className="btn btn-primary" data-testid="add-detail-photo-btn" onClick={() => triggerCamera('detail')}><Wrench size={16} /> {t.btnAddDetail}</button>}
        {mode !== 'outdoor' && <button className="btn btn-dark" data-testid="add-note-photo-btn" onClick={() => triggerCamera('note')}><PenLine size={16} /> {t.btnAddNote}</button>}
        <button className="btn btn-red" data-testid="add-video-btn" onClick={() => licensed() && vidRef.current.click()}><Video size={16} /> {t.btnVideo}</button>
        <button className="btn btn-outline" data-testid="gallery-btn" onClick={() => licensed() && galRef.current.click()}><ImagePlus size={16} /> {t.btnGallery}</button>
      </div>
      {ft.inventory && !slim && pv('inventory') && <div className="row">
        <button className={`btn btn-inventory ${hasModule('inventory') ? '' : 'locked'}`} data-testid="count-material-btn" onClick={openCounter}><Bolt size={16} /> {t.btnCount}</button>
        <button className="btn btn-outline" data-testid="count-history-btn" onClick={() => setShowHistory(true)}><History size={16} /> {t.btnHistory}</button>
      </div>}
      <input ref={invRef} type="file" accept="image/*" capture="environment" hidden data-testid="inventory-input" onChange={onInvFile} />
      {invPhoto && <InventoryCounter key={invKey} t={t} lang={lang} photo={invPhoto} onApply={applyInventory} onNext={nextInvPhoto} onClose={() => setInvPhoto(null)} />}
      {showRoute && project && <RoutePlanner t={t} project={project} center={mapRef.current?.getCenter() || { lat: project.lat, lng: project.lon }} onClose={() => setShowRoute(false)} onToggleDone={(id, done) => updateProject((p) => ({ ...p, reports: p.reports.map((r) => (r.id === id ? { ...r, done } : r)) }))} />}
      {showHistory && project && <CountHistory t={t} lang={lang} project={project} onClose={() => setShowHistory(false)} onOpenReport={(id) => { setShowHistory(false); selectReport(id); }} />}
      {showSettings && <SettingsDialog t={t} onClose={() => { setShowSettings(false); setLicTick((n) => n + 1); }} onChange={() => setLicTick((n) => n + 1)} />}
      <input ref={camRef} type="file" accept="image/*" capture="environment" hidden data-testid="camera-input" onChange={(e) => onFileInput(e, undefined, true)} />
      <input ref={mainGalRef} type="file" accept="image/*" hidden data-testid="main-gallery-input" onChange={(e) => onFileInput(e, 'main')} />
      <input ref={vidRef} type="file" accept="video/*" capture="environment" hidden data-testid="video-input" onChange={(e) => onFileInput(e, 'general')} />
      <input ref={galRef} type="file" accept="image/*,video/*" multiple hidden data-testid="gallery-input" onChange={(e) => onFileInput(e, 'general')} />
      {mode === 'photo' && <PhotoDocBar t={t} lang={lang} project={project} report={report} updateProject={updateProject} />}
      <PhotoList t={t} photos={report.photos} onUpdate={updatePhoto} onRole={setPhotoRole} onDelete={deletePhoto} onMemo={addPhotoMemo} sector={t[`fxS_${project.docSector}`] || ''} />

      <h3 className="sec"><span>04</span>{t.textTitle}<HelpBtn k="n:dictation" testid="sec04-help" title={t.helpHere} /></h3>
      <DictateField t={t} as="textarea" testid="report-text-input" placeholder={t.textPlaceholder} value={report.text} onChange={(v) => updateReport({ text: v })} />
      <div className="row memo-row">
        <VoiceRecorder t={t} testid="report-memo-btn" onDone={addReportMemo} />
      </div>
      {!slim && mode !== 'survey' && pv('materials') && <ReportMaterials t={t} project={project} report={report} updateReport={updateReport} />}
      {['building', 'pipe', 'survey'].includes(mode) && ft.lv && <LvBox t={t} project={project} report={report} updateReport={updateReport} />}
      {mode === 'pipe' && <CivilBox t={t} project={project} updateProject={updateProject} />}
      <MemoList t={t} memos={report.memos} prefix="report" onDelete={(mid) => updateReport((r) => ({ memos: r.memos.filter((m) => m.id !== mid) }))} />

      <div className="io-grid" data-testid="io-grid">
      <section className="io-col" data-testid="export-center">
      <h3 className="sec"><span>05</span>{t.exportTitle}<HelpBtn k="n:quick_pdf" testid="sec05-help" title={t.helpHere} /></h3>
      {ft.network && <NetQualityCard t={t} project={project} />}
      {!leisure && pv('signature') && <SignatureBox t={t} project={project} updateProject={updateProject} />}
      {['detect_inhouse', 'detect_roof'].includes(mode) && <DamageReport t={t} lang={lang} project={project} updateProject={updateProject} />}
      {!slim && pv('timesheet') && <TimesheetBox t={t} lang={lang} projects={projects} project={project} />}
      {leisure && <div className="row">
        <button className="btn btn-green" disabled={!!busy} data-testid="tour-book-last-btn" onClick={() => { const a = allTours(); return a.length ? run('bookLast', () => tourBook([a[a.length - 1]], project, t)) : toast.message(t.bookNoTrack); }}><BusyIcon k="bookLast" icon={<FileText size={16} />} /> {t.bookLast}</button>
        <button className="btn btn-green" disabled={!!busy} data-testid="tour-book-all-btn" onClick={() => { const a = allTours(); return a.length ? run('book', () => tourBook(a, project, t)) : toast.message(t.bookNoTrack); }}><BusyIcon k="book" icon={<Files size={16} />} /> {t.bookAll}</button>
        <button className="btn btn-dark" data-testid="tour-flyover-last-btn" onClick={() => { const a = allTours().filter((x) => x.pts?.length > 1); return a.length ? setFlyTrack(a[a.length - 1]) : toast.message(t.bookNoTrack); }}><MapIcon size={16} /> {t.flyLast}</button>
        {handoffOn() && <button className="btn btn-proj" data-testid="tour-handoff-btn" onClick={() => setShowHandoff(true)}><Monitor size={16} /> {t.hoTitle}</button>}
      </div>}
      {!leisure && <div className="row">
        {mode === 'garden' && <button className="btn btn-green" disabled={!!busy} data-testid="plot-pdf-btn" onClick={() => run('plot', () => plotPdf(t, project, report))}><BusyIcon k="plot" icon={<FileText size={16} />} /> {t.pkBtn}</button>}
        <button className="btn btn-red" disabled={!!busy} data-testid="export-pdf-single-btn" onClick={() => run('pdf1', () => exportPDF(project, [report], lang))}><BusyIcon k="pdf1" icon={<FileText size={16} />} /> {t.btnPdfSingle}</button>
        <button className="btn btn-red" disabled={!!busy} data-testid="export-pdf-all-btn" onClick={() => run('pdfAll', () => exportPDF(project, project.reports, lang, { cover: isProj }))}><BusyIcon k="pdfAll" icon={<Files size={16} />} /> {t.btnPdfAll}</button>
        <button className="btn btn-share" disabled={!!busy} data-testid="share-pdf-single-btn" onClick={() => run('share1', () => sharePdf(project, [report], lang))}><BusyIcon k="share1" icon={<Share2 size={16} />} /> {t.shareReport}</button>
        {isProj && <button className="btn btn-share" disabled={!!busy} data-testid="share-pdf-all-btn" onClick={() => run('shareAll', () => sharePdf(project, project.reports, lang, { cover: true }))}><BusyIcon k="shareAll" icon={<Share2 size={16} />} /> {t.shareProject}</button>}
      </div>}
      <div className="row">
        <button className="btn btn-accent" disabled={!!busy} data-testid="export-kmz-btn" onClick={() => run('kmz', () => exportKMZ(project, lang))}><BusyIcon k="kmz" icon={<Globe2 size={16} />} /> {t.btnKmzAll}</button>
        <a className="btn btn-green" data-testid="open-gmaps-btn" target="_blank" rel="noreferrer" href={`https://www.google.com/maps/search/?api=1&query=${report.lat},${report.lon}`}><MapIcon size={16} /> {t.btnOpenGmaps}</a>
      </div>
      <div className="row">
        {(ft.network || ft.fittings) && <button className="btn btn-green" disabled={!!busy} data-testid="export-xlsx-btn" onClick={() => run('xlsx', () => exportXLSX(project, lang))}><FileSpreadsheet size={16} /> {t.btnXlsx}</button>}
        {ft.maint && <button className="btn btn-outline" disabled={!!busy} data-testid="export-maint-xlsx-btn" onClick={() => (exportMaintXLSX(project, lang) ? toast.success(t.mwoDoneToast) : toast.message(t.mwoNone))}><Wrench size={16} /> {t.mwoBtn}</button>}
        {ft.tracks && (project.tracks || []).length > 0 && <button className="btn btn-outline" data-testid="export-log-csv-btn" onClick={() => exportLogCSV(project, lang)}><FileSpreadsheet size={16} /> {t.tlCsv}</button>}
        {ft.gas && <button className="btn btn-red" disabled={!!busy} data-testid="export-gas-xlsx-btn" onClick={() => (exportGasXLSX(project, lang) ? toast.success(t.glXlsxOk) : toast.message(t.glXlsxNone))}><FileSpreadsheet size={16} /> {t.glXlsxBtn}</button>}
        {ft.cadio && <button className="btn btn-outline" disabled={!!busy} data-testid="export-csv-btn" onClick={() => run('csv', () => exportCSV(project, lang))}><Sheet size={16} /> {t.btnCsvAufmass}</button>}
      </div>
      <div className="row">
        {ft.cadio && <button className="btn btn-dark" disabled={!!busy} data-testid="export-dxf-btn" onClick={() => run('dxf', () => exportDXF(project, lang))}><PencilRuler size={16} /> {t.btnDxf}</button>}
        {ft.cadio && <button className="btn btn-primary" disabled={!!busy} data-testid="export-geojson-btn" onClick={() => run('geojson', () => exportGeoJSON(project, lang))}><Earth size={16} /> {t.btnGeojson}</button>}
        <button className="btn btn-outline" disabled={!!busy} data-testid="export-gpx-btn" onClick={() => run('gpx', () => exportGPX(project, lang))}><Satellite size={16} /> {t.btnGpx}</button>
      </div>
      {ft.cadio && <div className="row">
        <button className="btn btn-proj" disabled={!!busy} data-testid="export-zip-btn" onClick={() => run('zip', () => exportOfficeZip(project, lang))}><BusyIcon k="zip" icon={<Package size={16} />} /> {t.btnZip}</button>
      </div>}
      <div className="row">
        <button className="btn btn-outline" data-testid="export-json-btn" onClick={() => run('json', () => exportJSON(project))}><Save size={16} /> {t.btnSaveJson}</button>
        <input ref={jsonRef} type="file" accept=".giss,.json,.txt,application/json,text/plain" hidden data-testid="json-input" onChange={importJSON} />
      </div>
      </section>
      <ImportCenter t={t} project={project} updateProject={updateProject} onBackup={importJSON} getCenter={() => mapRef.current?.getCenter()} />
      </div>
      <footer className="foot" data-testid="app-footer">
        <div>{project.reports.length} {t.reports} · {project.reports.reduce((n, r) => n + r.photos.length, 0)} {t.photos}</div>
        <div className="foot-brand">{APP_INFO.name} v{APP_INFO.version} · © {APP_INFO.publisher} · {APP_INFO.web}</div>
      </footer>
    </div>
  );
}

export default App;
